import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  PhoneXMarkIcon,
  PencilSquareIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import {
  getLiveGenerativeModel,
  startAudioConversation,
  ResponseModality,
  Schema,
  type LiveSession,
  type AudioConversationController,
  type Tool,
  type FunctionCall,
  type FunctionResponse,
} from "firebase/ai";
import { aiGoogle } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import {
  addNoteLog,
  addRecoveryLog,
  addNiggleLog,
  addPerformanceLog,
  addTrainingLog,
  addLogIDToDayPost,
  generateDateID,
  updateTrainingLog,
  updateNoteLog,
  updateRecoveryLog,
  updateNiggleLog,
  updatePerformanceLog,
  getTrainingLog,
  getNoteLog,
  getRecoveryLog,
  getNiggleLog,
  getPerformanceLog,
  getDayPosts,
  getLast28Days,
} from "../services/dayPostService";
import { generateAthleteReport } from "../utils/athleteReportUtils";
import { formatMeasurementWithUnit } from "../utils/unitUtils";
import { sessionService } from "../services/sessionService";
import { activityService } from "../services/activityService";
import { GlobalActivityService } from "../services/globalActivityService";
import type {
  ActivityType,
  ActivityVariableDefinition,
  TrainingType,
} from "../types/activity";
import { trainingTypeService } from "../services/trainingTypeService";
import type {
  ActivityMeasurement,
  NoteLog,
  RecoveryLog,
  NiggleLog,
  PerformanceLog,
  TrainingLog,
  Session,
} from "../types/post";
import {
  DEFAULT_UNITS_METRIC,
  DEFAULT_UNITS_IMPERIAL,
} from "../utils/unitUtils";
import NoteLogCard from "./DayPost/NoteLogCard";
import RecoveryLogCard from "./DayPost/RecoveryLogCard";
import NiggleLogCard from "./DayPost/NiggleLogCard";
import PerformanceLogCard from "./DayPost/PerformanceLogCard";
import TrainingLogCard from "./DayPost/TrainingLogCard";
import SessionActivitiesTable from "./sessions/SessionActivitiesTable";
import { SessionNotes } from "./sessions/SessionNotesMarkdown";
import AddNoteLogForm from "./DayPost/AddNoteLogForm";
import AddRecoveryLogForm from "./DayPost/AddRecoveryLogForm";
import AddNiggleLogForm from "./DayPost/AddNiggleLogForm";
import AddPerformanceLogForm from "./DayPost/AddPerformanceLogForm";
import TrainingLogForm from "./DayPost/training-log/TrainingLogForm";
import peakIconBlack from "../assets/icon-black.png";

interface OlyChatProps {
  isOpen: boolean;
  onClose: () => void;
}

type CallStatus = "idle" | "connecting" | "active" | "ending";

// Mirror of `recoveryMethodsWithPoints` in
// `src/components/DayPost/AddRecoveryLogForm.tsx`. Keep in sync.
const RECOVERY_METHODS_WITH_POINTS: { name: string; points: number }[] = [
  { name: "Meditation", points: 4 },
  { name: "Foam Rolling", points: 3 },
  { name: "Physiotherapy", points: 4 },
  { name: "Recovery Jog", points: 4 },
  { name: "Sauna", points: 4 },
  { name: "Stretching and Mobility", points: 4 },
  { name: "Supplements", points: 1 },
  { name: "Breathing", points: 2 },
  { name: "Contrast Therapy", points: 4 },
  { name: "Compression Therapy", points: 4 },
  { name: "Nap", points: 3 },
  { name: "Grounding", points: 1 },
  { name: "Ice Bath", points: 4 },
  { name: "Massage", points: 5 },
  { name: "Red Light Therapy", points: 4 },
  { name: "Hyperbaric Oxygen Therapy", points: 3 },
  { name: "Cold Showers", points: 2 },
  { name: "Other", points: 1 },
];

const RECOVERY_METHOD_NAMES = RECOVERY_METHODS_WITH_POINTS.map((m) => m.name);

const RECOVERY_POINTS_LOOKUP = new Map(
  RECOVERY_METHODS_WITH_POINTS.map((m) => [m.name.toLowerCase(), m] as const),
);

const resolveRecoveryMethod = (input: unknown): string | null => {
  if (typeof input !== "string") return null;
  const match = RECOVERY_POINTS_LOOKUP.get(input.trim().toLowerCase());
  return match ? match.name : null;
};

const calculateRecoveryPoints = (methods: string[]): number =>
  methods.reduce((total, name) => {
    const match = RECOVERY_POINTS_LOOKUP.get(name.toLowerCase());
    return total + (match?.points ?? 0);
  }, 0);

// Mirror of getPainResponse / getImpactResponse in
// `src/components/DayPost/AddNiggleLogForm.tsx`.
const NIGGLE_PAIN_LABELS = [
  "No pain at all",
  "Very mild discomfort",
  "Mild pain",
  "Noticeable pain",
  "Moderate pain",
  "Moderately strong pain",
  "Strong pain",
  "Very strong pain",
  "Extremely strong pain",
  "Unbearable pain",
];

const NIGGLE_IMPACT_LABELS = [
  "No impact on performance",
  "Barely noticeable impact",
  "Slight impact on performance",
  "Noticeable impact",
  "Moderate impact on performance",
  "Significant impact",
  "Major impact on performance",
  "Severe impact",
  "Extremely limiting",
  "Cannot perform at all",
];

const niggleLabel = (value: number, labels: string[]): string =>
  labels[Math.max(1, Math.min(10, Math.round(value))) - 1] ?? labels[0];

const coerceNiggleScale = (value: unknown): number | null => {
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return null;
  const rounded = Math.round(num);
  if (rounded < 1 || rounded > 10) return null;
  return rounded;
};

// Mirror of getPerformanceResponse in
// `src/components/DayPost/AddPerformanceLogForm.tsx`.
const PERFORMANCE_EXPECTATION_LABELS = [
  "Far below expectations",
  "Well below expectations",
  "Below expectations",
  "Slightly below expectations",
  "Met expectations",
  "Slightly above expectations",
  "Above expectations",
  "Well above expectations",
  "Exceeded expectations",
  "Personal Best",
];

const PERFORMANCE_TAG_OPTIONS = [
  { code: "PB", label: "Personal Best" },
  { code: "SB", label: "Season's Best" },
];

const PERFORMANCE_TAG_CODES = PERFORMANCE_TAG_OPTIONS.map((t) => t.code);

// Mirror of the muscular/respiratory effort scales in
// `src/components/DayPost/AddTrainingLogForm.tsx`.
const TRAINING_MUSCULAR_LABELS = [
  "Very little muscle effort",
  "Light muscle effort",
  "Mild muscle effort",
  "Moderate muscle effort, comfortable",
  "Moderately hard muscle effort",
  "Hard muscle effort, noticeable fatigue",
  "Very hard muscle effort, but manageable",
  "Very heavy effort, difficult to sustain",
  "Near-maximal effort, close to muscular failure",
  "Maximal muscle effort, failure or could not continue",
];

const TRAINING_RESPIRATORY_LABELS = [
  "Easy at rest",
  "Slight change in breathing",
  "Breathing a little faster but comfortable",
  "Breathing moderately heavier, still easy",
  "Breathing clearly harder but controlled",
  "Breathing hard, noticeable effort",
  "Very hard breathing, but manageable",
  "Very breathless, difficult to sustain",
  "Near-maximal breathlessness",
  "Maximal breathlessness, had to stop or recover",
];

const trainingScaleLabel = (value: number, labels: string[]): string => {
  const idx = Math.max(1, Math.min(labels.length, Math.round(value))) - 1;
  return labels[idx] ?? "";
};

const coerceTrainingScale = (value: unknown): number | null => {
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return null;
  const rounded = Math.round(num);
  if (rounded < 1 || rounded > 10) return null;
  return rounded;
};

const formatDurationMinutes = (minutes: number): string => {
  if (!Number.isFinite(minutes) || minutes <= 0) return "";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  return `${m}m`;
};

// Normalise the variables of an ActivityType (which may be legacy strings or
// ActivityVariableDefinition objects) into a single shape with name +
// defaultUnit. Mirrors the conversion done in SessionBuilder/AddTrainingLogForm.
const normalizeActivityVariables = (
  variables: ActivityType["variables"],
  isImperial: boolean,
): ActivityVariableDefinition[] => {
  const defaults = isImperial ? DEFAULT_UNITS_IMPERIAL : DEFAULT_UNITS_METRIC;
  return (variables || []).map((variable) => {
    if (typeof variable === "string") {
      return {
        name: variable,
        defaultUnit: defaults[variable] || "",
      };
    }
    return {
      name: variable.name,
      defaultUnit: variable.defaultUnit || defaults[variable.name] || "",
    };
  });
};

// Build a compact lookup keyed by ActivityTypeID so the AI's chosen IDs can be
// validated against the user's actually-available activities.
type ActivityLookupEntry = {
  id: string;
  name: string;
  variables: ActivityVariableDefinition[];
  globalCategoryID?: string;
};

const buildActivityLookup = (
  activities: ActivityType[],
  isImperial: boolean,
): Map<string, ActivityLookupEntry> => {
  const map = new Map<string, ActivityLookupEntry>();
  for (const activity of activities) {
    if (!activity.id) continue;
    map.set(activity.id, {
      id: activity.id,
      name: activity.name,
      variables: normalizeActivityVariables(activity.variables, isImperial),
      ...(activity.isFromPack && activity.categoryId
        ? { globalCategoryID: activity.categoryId }
        : {}),
    });
  }
  return map;
};

// A single set sitting in the in-memory "pending sets" list for a training
// log, waiting for the user to confirm before we persist it as a Session.
// Mirrors one entry of ActivityMeasurementList but keeps the denormalised
// `activityName` so we can render + read the list back without another
// lookup pass.
type PendingSet = {
  ActivityTypeID: string;
  activityName: string;
  variables: {
    name: string;
    measurement: number | null;
    unit?: string;
  }[];
  globalCategoryID?: string;
};

// Normalise a raw set object coming from the model against the user's
// activity lookup. Returns a validated PendingSet plus the names of any
// variables that didn't match the chosen activity (we silently drop those
// but surface them so the model knows). Returns an error string when the
// ActivityTypeID itself is invalid.
type NormalizeSetResult =
  | { ok: true; set: PendingSet; ignoredVariables: string[] }
  | { ok: false; error: string; unknownActivityID?: string };

const normalizePendingSet = (
  raw: unknown,
  activityLookup: Map<string, ActivityLookupEntry>,
): NormalizeSetResult => {
  if (!raw || typeof raw !== "object") {
    return { ok: false, error: "Set entry must be an object." };
  }
  const typed = raw as { ActivityTypeID?: unknown; variables?: unknown };
  const activityTypeID =
    typeof typed.ActivityTypeID === "string" ? typed.ActivityTypeID.trim() : "";
  if (!activityTypeID) {
    return {
      ok: false,
      error: "Set is missing an ActivityTypeID.",
    };
  }
  const lookup = activityLookup.get(activityTypeID);
  if (!lookup) {
    return {
      ok: false,
      error: `Unknown ActivityTypeID "${activityTypeID}". Use an ID from the 'Available activities' list.`,
      unknownActivityID: activityTypeID,
    };
  }

  const allowedVariableNames = new Set(
    lookup.variables.map((v) => v.name.toLowerCase()),
  );
  const defaultUnitByVariable = new Map(
    lookup.variables.map((v) => [v.name.toLowerCase(), v.defaultUnit]),
  );

  const rawVariables = Array.isArray(typed.variables) ? typed.variables : [];
  const variables: PendingSet["variables"] = [];
  const ignoredVariables: string[] = [];
  for (const variable of rawVariables) {
    if (!variable || typeof variable !== "object") continue;
    const v = variable as {
      name?: unknown;
      measurement?: unknown;
      unit?: unknown;
    };
    const rawName = typeof v.name === "string" ? v.name.trim() : "";
    if (!rawName) continue;
    if (!allowedVariableNames.has(rawName.toLowerCase())) {
      const tag = `${lookup.name}.${rawName}`;
      if (!ignoredVariables.includes(tag)) ignoredVariables.push(tag);
      continue;
    }
    const lookupVar = lookup.variables.find(
      (vv) => vv.name.toLowerCase() === rawName.toLowerCase(),
    );
    const canonicalName = lookupVar?.name ?? rawName;

    let measurement: number | null = null;
    if (v.measurement !== undefined && v.measurement !== null) {
      const num =
        typeof v.measurement === "number"
          ? v.measurement
          : Number(v.measurement);
      measurement = Number.isFinite(num) ? num : null;
    }

    let unit =
      typeof v.unit === "string" && v.unit.trim() ? v.unit.trim() : undefined;
    if (!unit) {
      const defaultUnit = defaultUnitByVariable.get(
        canonicalName.toLowerCase(),
      );
      if (defaultUnit) unit = defaultUnit;
    }

    variables.push({
      name: canonicalName,
      measurement,
      ...(unit ? { unit } : {}),
    });
  }

  return {
    ok: true,
    set: {
      ActivityTypeID: lookup.id,
      activityName: lookup.name,
      variables,
      ...(lookup.globalCategoryID
        ? { globalCategoryID: lookup.globalCategoryID }
        : {}),
    },
    ignoredVariables,
  };
};

// Human-readable summary of a pending set, used both in the UI and in
// function-call responses so the model can read the list back to the user.
const describePendingSet = (set: PendingSet): string => {
  const parts = set.variables
    .map((v) => {
      if (v.measurement === null || v.measurement === undefined) {
        return `${v.name} –`;
      }
      return `${v.name} ${formatMeasurementWithUnit(v.measurement, v.unit, v.name)}`;
    })
    .filter((p) => p.length > 0);
  return parts.length > 0
    ? `${set.activityName}: ${parts.join(", ")}`
    : set.activityName;
};

// Ordered, readable rendering of a pending-sets list suitable for a voice
// read-back. Numbered 1-based so the model can say "the second set" etc.
const describePendingSets = (sets: PendingSet[]): string =>
  sets.map((s, i) => `${i + 1}. ${describePendingSet(s)}`).join("\n");

const resolvePerformanceTag = (input: unknown): string | null => {
  if (typeof input !== "string") return null;
  const normalized = input.trim().toLowerCase();
  if (!normalized) return null;
  for (const tag of PERFORMANCE_TAG_OPTIONS) {
    if (
      normalized === tag.code.toLowerCase() ||
      normalized === tag.label.toLowerCase()
    ) {
      return tag.code;
    }
  }
  return null;
};

const OLY_BASE_SYSTEM_INSTRUCTION = [
  "You are Oly, a concise AI voice assistant inside the Peak X training app.",
  "Help athletes with training, recovery, wellness, and quick questions.",
  "Speak naturally, like a direct teammate on a phone call - not a polite",
  "customer-service bot.",
  "",
  "Core style:",
  "- Bias HARD toward action. If the user gives a clear directive and you",
  "  have everything you need, just do it and confirm after the fact. Do",
  "  NOT ask 'should I save it?', 'want me to go ahead?', 'are you sure?',",
  "  or read the full payload back before acting.",
  "- Replies must be short - usually one short sentence. Skip filler",
  "  pleasantries ('sure thing', 'absolutely', 'of course', 'happy to",
  "  help', 'great question', 'no problem', etc.).",
  "- Only ask a follow-up question when a REQUIRED field is genuinely",
  "  missing or the request is ambiguous. Never ask for optional fields.",
  "- After a tool succeeds, give a one-liner acknowledgement ('Noted.',",
  "  'Saved.', 'Logged a 30 min easy run for today.') and stop.",
  "- If the user's question is unclear, ask ONE brief clarifying question.",
  "- Reasonable defaults over questions: if the user doesn't specify a",
  "  date, assume today. If they don't specify units, use their default",
  "  measurement system. If a rating is implied ('easy run' -> muscular",
  "  3, respiratory 3; 'hard session' -> 7/7), fill it in yourself.",
  "",
  "When the call first connects you will receive a short system-style",
  "message indicating the call has started. You MUST speak first. Your",
  'very first spoken line must be exactly: "Hey, how can I help you?"',
  "- nothing before it, no preamble, no name, no other greeting. After",
  "that opener, wait for the user to respond and continue normally.",
  "",
  "Tools you can call:",
  "- `endCall`: Use when the user asks to hang up, end, stop, or finish the call",
  "  (e.g. 'hang up', 'goodbye', 'that's all'). Say a short farewell first, then",
  "  call the tool. Do not call it for any other reason.",
  "- `getAthleteReport`: Fetch the user's recent training, recovery, wellness,",
  "  sleep, load, niggle and Peak Score data (last 7 days, computed from a",
  "  28-day window). Call this WHENEVER the user asks anything about their",
  "  past data, how they're doing, sleep, training load, recovery, fatigue,",
  "  peak score, niggles, soreness, mood, stress, trends, etc. Don't call it",
  "  for unrelated chat or for the write tools below. Call it once at the",
  "  start of the relevant conversation - the result is cached for the rest",
  "  of the call. After the tool returns, summarise the user's question",
  "  in 1-2 spoken sentences using the report; do NOT read the whole report",
  "  out loud verbatim.",
  "- `getActivitiesAndSessionTypes`: Fetch the user's available activity types",
  "  and session types so you can build pending sets. ONLY call this",
  "  immediately before you need to gather session details for `addSets` /",
  "  `saveSession` (i.e. after the user has agreed to add session details to",
  "  a saved training log). Don't call it speculatively. The result is",
  "  cached for the rest of the call.",
  "- `addNote`: Saves a personal note to the user's day post. Workflow:",
  "    1. If the user gave the note content in the same utterance (e.g. 'make",
  "       a note that says going to the gym today'), IMMEDIATELY call the",
  "       tool with that text - do NOT confirm first. Keep the user's",
  "       wording verbatim (strip only leading filler like 'that says').",
  "    2. If the user asked for a note but didn't give the content, ask",
  "       once for the content, then save as soon as they give it. Do not",
  "       read the note back before saving.",
  "    3. The note defaults to today. If the user mentions a different day",
  "       (e.g. 'yesterday', 'on Monday', 'for the 18th'), compute the correct",
  "       date in YYYYMMDD format using the date context below and pass it as",
  "       the `dateID` argument. Otherwise omit `dateID`.",
  "    4. After the tool succeeds, say just 'Noted.' or similar one-liner.",
  "- `addRecovery`: Logs a recovery session to the user's day post. Workflow:",
  "    1. If the user already named the recovery method(s) (e.g. 'log a 20",
  "       minute sauna and an ice bath'), IMMEDIATELY call the tool. Do not",
  "       read methods back or ask for confirmation. Methods MUST come from",
  "       the allowed list shown below - map close synonyms to the nearest",
  "       option (e.g. 'stretching' -> 'Stretching and Mobility') or fall",
  "       back to 'Other' for unmatched methods.",
  "    2. If the user asked for 'log my recovery' with no method named,",
  "       ask once which methods, then save. Never ask about notes - they're",
  "       optional; only include if the user volunteered content about the",
  "       session.",
  "    3. The log defaults to today. If the user mentions a different day, pass",
  "       the correct `dateID` in YYYYMMDD format; otherwise omit `dateID`.",
  "    4. After the tool succeeds, give a one-line confirmation, mentioning",
  "       total recovery points if the tool response includes them.",
  "- `addNiggle`: Logs a niggle (minor injury or area of pain/discomfort) to the",
  "  user's day post. Workflow:",
  "    1. You need three things: (a) where/what it feels like (the `notes`),",
  "       (b) a pain rating 1 (no pain) to 10 (unbearable), (c) an",
  "       impact-on-performance rating 1 (none) to 10 (cannot perform).",
  "       Use the scales described below. Translate qualitative wording",
  "       ('mild ache', 'bad pain', 'barely affecting me') directly into",
  "       sensible 1-10 numbers yourself - don't ask the user for a",
  "       number if their wording already implies one.",
  "    2. Ask only for fields that are genuinely missing after the user's",
  "       first description. Combine questions into one short sentence",
  "       (e.g. 'How bad is it and is it affecting your training?') -",
  "       never ask them three separate questions.",
  "    3. Once you have all three, call the tool IMMEDIATELY. Do not read",
  "       back or ask 'save it?' - just save.",
  "    4. The log defaults to today. If the user mentions a different day, pass",
  "       the correct `dateID` in YYYYMMDD format; otherwise omit `dateID`.",
  "    5. After the tool succeeds, give a one-liner acknowledgement.",
  "- `addPerformance`: Logs a performance (a race, time trial, key session, or",
  "  any meaningful result the athlete wants to record). Workflow:",
  "    1. You need: (a) `performanceTitle` - short name (e.g. '5k parkrun',",
  "       'squat 1RM test'). (b) `performanceResult` - the result in the",
  "       user's own wording ('18:42', '215m', etc.). (c)",
  "       `performanceExpectation` 1-10 using the scale below - infer it",
  "       from their tone if they volunteered any ('stoked' -> 8+, 'rough",
  "       day' -> 2-3). Only ask explicitly if their wording is neutral.",
  "       (d) optional `notes` - include only if the user gave extra",
  "       colour; never ask for them.",
  "    2. PB/SB tag is the ONE genuinely ambiguous thing here. If the user",
  "       already made it clear ('that's a PB', 'not my best', etc.), set",
  "       `tag` accordingly - include 'SB' automatically whenever 'PB' is",
  "       set. Only when the user's statement is silent on this AND the",
  "       result sounds notable, ask a single question: 'PB or season's",
  "       best?'. For routine-sounding results, just omit `tag` and save.",
  "    3. Once you have the required fields (and a PB/SB answer if you",
  "       asked), call the tool IMMEDIATELY. No readback, no 'save it?'.",
  "    4. Defaults to today. If the user mentions a different day, pass the",
  "       correct `dateID` in YYYYMMDD format; otherwise omit `dateID`.",
  "    5. After the tool succeeds, give a one-line confirmation.",
  "- `addTraining`: Logs a training session (a workout, run, ride, gym session,",
  "  swim, etc.). Workflow:",
  "    1. Required fields: (a) `description` - short tag (e.g. 'Easy run',",
  "       'Threshold intervals', 'Strength', 'Long ride'). (b) `duration`",
  "       in minutes (integer). (c) `muscular` 1-10. (d) `respiratory`",
  "       1-10. For (c) and (d), translate qualitative wording to numbers",
  "       yourself: 'easy' -> 2-3, 'moderate' -> 4-5, 'hard' -> 7, 'very",
  "       hard / all out' -> 9-10. Only ask when the user gave no effort",
  "       signal at all.",
  "    2. Optional fields: `notes` - only if the user gave extra detail;",
  "       never ask. `pain` - set true only if the user said something",
  "       hurt during the session. `injured` - set true only if the user",
  "       said they got injured.",
  "    3. Calculated RPE = average of muscular and respiratory. If RPE",
  "       >= 7.5, ALSO ask `trainingPerformed` 1-10 ('how did it go vs",
  "       what you planned?'). For RPE < 7.5, omit `trainingPerformed`",
  "       entirely - don't ask.",
  "    4. Once required fields are in hand, call the tool IMMEDIATELY. No",
  "       readback, no 'save it?'.",
  "    5. Defaults to today. If the user mentions a different day, pass the",
  "       correct `dateID` in YYYYMMDD format; otherwise omit `dateID`.",
  "    6. After the tool succeeds, give a one-line confirmation. The tool",
  "       returns a `logID` - remember it. Then briefly offer to add session",
  "       details ('want to add sets?'). If yes, follow the pending-sets",
  "       workflow below; if no, move on.",
  "- Pending-sets workflow (`addSets`, `updateSet`, `removeSet`, `saveSession`):",
  "  Attach a structured breakdown (activities + sets) to a training log",
  "  that was already saved by `addTraining`. You build up an in-memory",
  "  pending-sets list that is shown to the user in the app UI as you go,",
  "  and commit to the database with `saveSession`. Call these tools only",
  "  after `addTraining` has returned a `trainingLogID` AND you have called",
  "  `getActivitiesAndSessionTypes` in this call so you know the user's",
  "  available ActivityTypeIDs.",
  "    a. Use ONLY ActivityTypeIDs and variable names from the result of",
  "       `getActivitiesAndSessionTypes`; never invent IDs.",
  "    b. One entry in `sets` = ONE set. Repeat the same ActivityTypeID for",
  "       multiple sets (e.g. 3 squat entries for 3 sets of squats).",
  "    c. For each variable, set `measurement` to a number if the user gave",
  "       one, or null if not. Always include `unit` using the user's",
  "       measurement system.",
  "    d. `addSets`: call IMMEDIATELY when the user describes new set(s).",
  "       Shorthand like '3x5 at 100kg' = 3 sets, 5 reps, 100kg each - just",
  "       expand and call `addSets`. Don't confirm shorthand. You can call",
  "       `addSets` repeatedly as the user describes more sets. After each",
  "       call, the tool response includes the full updated pending list",
  "       with 1-based set numbers - use this to keep track.",
  "    e. `updateSet`: call when the user corrects a set they've already",
  "       described (e.g. 'no, make the second set 12 reps'). `setNumber`",
  "       is 1-based. You must provide the full replacement set.",
  "    f. `removeSet`: call when the user drops a set (e.g. 'scratch that",
  "       last set'). `setNumber` is 1-based. Remaining sets renumber.",
  "    g. Unknown values use `null` - never ask the user to fill in",
  "       optional variables.",
  "    h. When the user signals they're done adding sets (e.g. 'that's it',",
  "       'save it', 'done'), call `saveSession` IMMEDIATELY. Do NOT read",
  "       the full set list back - the UI already displays it for them.",
  "       Only ask one short 'save it?' if the user hasn't explicitly",
  "       signalled done and it's unclear whether more sets are coming.",
  "    i. `saveSession`: persists the pending list as a Session linked to",
  "       the training log. After it succeeds, give a one-line",
  "       confirmation. The pending list is cleared automatically.",
].join(" ");

const buildSystemInstruction = (
  now: Date,
  context: {
    isImperial: boolean;
  },
): string => {
  const isoDate = now.toISOString().slice(0, 10);
  const dayName = now.toLocaleDateString("en-US", { weekday: "long" });
  const todayDateID = generateDateID(now);
  const methodsList = RECOVERY_METHODS_WITH_POINTS.map(
    (m) => `${m.name} (${m.points} pts)`,
  ).join(", ");
  const painScale = NIGGLE_PAIN_LABELS.map(
    (label, idx) => `${idx + 1}=${label}`,
  ).join("; ");
  const impactScale = NIGGLE_IMPACT_LABELS.map(
    (label, idx) => `${idx + 1}=${label}`,
  ).join("; ");
  const expectationScale = PERFORMANCE_EXPECTATION_LABELS.map(
    (label, idx) => `${idx + 1}=${label}`,
  ).join("; ");
  const performanceTags = PERFORMANCE_TAG_OPTIONS.map(
    (t) => `${t.code} (${t.label})`,
  ).join(", ");
  const muscularScale = TRAINING_MUSCULAR_LABELS.map(
    (label, idx) => `${idx + 1}=${label}`,
  ).join("; ");
  const respiratoryScale = TRAINING_RESPIRATORY_LABELS.map(
    (label, idx) => `${idx + 1}=${label}`,
  ).join("; ");

  const measurementSystem = context.isImperial ? "Imperial" : "Metric";
  const defaultUnits = context.isImperial
    ? DEFAULT_UNITS_IMPERIAL
    : DEFAULT_UNITS_METRIC;

  return [
    OLY_BASE_SYSTEM_INSTRUCTION,
    "",
    `Date context: today is ${dayName} ${isoDate} (dateID ${todayDateID}).`,
    "",
    `Allowed recovery methods (use these exact names): ${methodsList}.`,
    "",
    `Niggle pain scale (1-10): ${painScale}.`,
    `Niggle impact-on-performance scale (1-10): ${impactScale}.`,
    "",
    `Performance expectation scale (1-10): ${expectationScale}.`,
    `Performance tag codes: ${performanceTags}.`,
    "",
    `Training muscular effort scale (1-10): ${muscularScale}.`,
    `Training respiratory effort scale (1-10): ${respiratoryScale}.`,
    "",
    `User measurement system: ${measurementSystem}. Always use these default units`,
    "for each variable unless the user explicitly says different units, and never",
    "mix metric and imperial in the same session:",
    JSON.stringify(defaultUnits),
    "",
    "The user's available activity types and session types are NOT included up",
    "front to keep the prompt small. Call `getActivitiesAndSessionTypes` to",
    "fetch them on demand right before building the pending-sets list.",
  ].join(" ");
};

const OLY_TOOLS: Tool[] = [
  {
    functionDeclarations: [
      {
        name: "endCall",
        description:
          "Hang up and end the current voice call with the user. Call this only when the user explicitly asks to end, stop, hang up, or finish the call.",
      },
      {
        name: "getAthleteReport",
        description:
          "Fetch the user's recent training, recovery, wellness, sleep, load, niggle and Peak Score data (a structured summary of the last 7 days, computed from a 28-day window). Call this whenever the user asks anything about their past data, sleep, training load, recovery, fatigue, peak score, niggles, soreness, mood, stress, or trends - and BEFORE attempting to answer such questions. The result is cached for the rest of the call so calling it again is cheap. Returns a `report` string (markdown) summarising the user's recent state, and a small `summary` object with key headline numbers. Do NOT read the report verbatim; use it to ground a short, conversational reply.",
        parameters: Schema.object({
          properties: {
            focus: Schema.string({
              description:
                "Optional one-word hint about what the user asked, used only to help you frame the spoken reply. Examples: 'sleep', 'load', 'recovery', 'wellness', 'peak', 'niggle', 'general'.",
            }),
          },
          optionalProperties: ["focus"],
        }),
      },
      {
        name: "getActivitiesAndSessionTypes",
        description:
          "Fetch the user's available activity types and session types so you can build the pending-sets list (via `addSets`). Only call this immediately before you need to gather session-detail breakdowns (i.e. after the user has agreed to add session details to a saved training log). The result is cached for the rest of the call. Returns `activities` (each with ActivityTypeID, name, optional globalCategoryID, and variables) and `sessionTypes` (each with id and name).",
      },
      {
        name: "addNote",
        description:
          "Save a personal note to the user's day post. Only call this AFTER you have confirmed with the user (out loud) what the note should say and they have agreed to save it. The note will be persisted to their training journal.",
        parameters: Schema.object({
          properties: {
            notes: Schema.string({
              description:
                "The full text content of the note, exactly as the user wants it saved. Do not paraphrase or summarize.",
            }),
            dateID: Schema.string({
              description:
                "Optional. The date the note belongs to, in YYYYMMDD format (e.g. '20260422' for 22 April 2026). Omit this field to default to today.",
            }),
          },
          optionalProperties: ["dateID"],
        }),
      },
      {
        name: "addRecovery",
        description:
          "Log a recovery session to the user's day post. Only call this AFTER you have confirmed (out loud) which recovery methods to log and the user has agreed to save it. Points are calculated automatically from the selected methods.",
        parameters: Schema.object({
          properties: {
            selectedRecovery: Schema.array({
              description: `One or more recovery methods. Each value MUST be one of: ${RECOVERY_METHOD_NAMES.join(", ")}. Use 'Other' for anything that doesn't fit.`,
              items: Schema.string(),
            }),
            notes: Schema.string({
              description:
                "Optional notes about the recovery session, in the user's own words. Omit if the user does not provide any notes.",
            }),
            dateID: Schema.string({
              description:
                "Optional. The date the recovery belongs to, in YYYYMMDD format (e.g. '20260422' for 22 April 2026). Omit this field to default to today.",
            }),
          },
          optionalProperties: ["notes", "dateID"],
        }),
      },
      {
        name: "addNiggle",
        description:
          "Log a niggle (a minor injury, ache or area of pain/discomfort) to the user's day post. Only call this AFTER you have gathered the location/description, pain rating, and impact rating, and the user has confirmed they want it saved.",
        parameters: Schema.object({
          properties: {
            pain: Schema.integer({
              description:
                "Pain level on a 1-10 scale (1 = no pain at all, 10 = unbearable pain). Use the niggle pain scale described in the system instruction.",
            }),
            impact: Schema.integer({
              description:
                "Impact on performance on a 1-10 scale (1 = no impact, 10 = cannot perform at all). Use the niggle impact scale described in the system instruction.",
            }),
            notes: Schema.string({
              description:
                "Description of the niggle - typically location (e.g. 'left calf'), what it feels like, when it started, and what makes it better or worse. Use the user's own words where possible.",
            }),
            dateID: Schema.string({
              description:
                "Optional. The date the niggle belongs to, in YYYYMMDD format (e.g. '20260422' for 22 April 2026). Omit this field to default to today.",
            }),
          },
          optionalProperties: ["dateID"],
        }),
      },
      {
        name: "addPerformance",
        description:
          "Log a performance/result (race, time trial, key session, PB attempt, etc.) to the user's day post. Only call this AFTER you have gathered the title, result, and how it compared to expectations, and the user has confirmed they want it saved.",
        parameters: Schema.object({
          properties: {
            performanceTitle: Schema.string({
              description:
                "Short name for the performance, e.g. '5k parkrun', 'Squat 1RM test', 'Sunday club race'. Use the user's wording.",
            }),
            performanceResult: Schema.string({
              description:
                "The actual result as the user said it - a time like '18:42', a distance like '215 m', a score like '425 W', placing, etc. Keep their wording; do not convert units.",
            }),
            performanceExpectation: Schema.integer({
              description:
                "How the performance compared to expectations on a 1-10 scale (1 = far below expectations, 5 = met expectations, 10 = personal best). Use the performance expectation scale described in the system instruction.",
            }),
            notes: Schema.string({
              description:
                "Optional notes about how the performance went (conditions, how it felt, tactics, etc.) in the user's own words. Omit if the user does not provide any notes.",
            }),
            tag: Schema.array({
              description: `Optional tags. Each value MUST be one of: ${PERFORMANCE_TAG_CODES.join(", ")}. Use 'PB' for a personal best and 'SB' for a season's best. Omit if neither applies.`,
              items: Schema.string(),
            }),
            dateID: Schema.string({
              description:
                "Optional. The date the performance happened, in YYYYMMDD format (e.g. '20260422' for 22 April 2026). Omit this field to default to today.",
            }),
          },
          optionalProperties: ["notes", "tag", "dateID"],
        }),
      },
      {
        name: "addTraining",
        description:
          "Log a training session (workout, run, ride, gym session, swim, etc.) to the user's day post. Only call this AFTER you have gathered the description, duration, and effort levels, and the user has confirmed they want it saved.",
        parameters: Schema.object({
          properties: {
            description: Schema.string({
              description:
                "Short label for the session, saved as a tag. Examples: 'Easy run', 'Threshold intervals', 'Long ride', 'Strength', 'Recovery swim'. Use the user's wording where possible.",
            }),
            duration: Schema.integer({
              description:
                "Duration of the session in MINUTES (integer). If the user gives hours and minutes, convert (e.g. '1h 30min' -> 90).",
            }),
            muscular: Schema.integer({
              description:
                "How hard the muscles worked on a 1-10 scale. Use the training muscular effort scale described in the system instruction.",
            }),
            respiratory: Schema.integer({
              description:
                "How hard the user was breathing on a 1-10 scale. Use the training respiratory effort scale described in the system instruction.",
            }),
            trainingPerformed: Schema.integer({
              description:
                "Optional. How well the user executed the planned session on a 1-10 scale (1 = barely got through it, 10 = nailed it). Only ask for and include this when the calculated RPE ((muscular + respiratory) / 2) is 7.5 or higher. Omit otherwise.",
            }),
            notes: Schema.string({
              description:
                "Optional notes about how the session went, in the user's own words. Omit if the user did not provide any.",
            }),
            pain: Schema.boolean({
              description:
                "Optional. True if the user mentioned any pain during the session. Omit otherwise.",
            }),
            injured: Schema.boolean({
              description:
                "Optional. True if the user said they got injured during this session. Omit otherwise.",
            }),
            dateID: Schema.string({
              description:
                "Optional. The date the session happened, in YYYYMMDD format (e.g. '20260422' for 22 April 2026). Omit this field to default to today.",
            }),
          },
          optionalProperties: [
            "trainingPerformed",
            "notes",
            "pain",
            "injured",
            "dateID",
          ],
        }),
      },
      {
        name: "addSets",
        description:
          "Append one or more sets to the in-memory pending-sets list for a training log. The pending-sets list is shown to the user in the app UI so they can see what you're about to save, and is NOT persisted to the database until `saveSession` is called. Use this to build up the session set by set as the user describes their workout. You can call `addSets` multiple times to add more sets later. ActivityTypeID values MUST come from `getActivitiesAndSessionTypes`.",
        parameters: Schema.object({
          properties: {
            trainingLogID: Schema.string({
              description:
                "REQUIRED. The `logID` returned from the matching `addTraining` call earlier in this conversation. All pending sets for that training log are grouped together.",
            }),
            sets: Schema.array({
              description:
                "One or more sets to append to the pending list. CRITICAL: one entry equals exactly ONE set; to add 3 sets of the same activity, provide 3 entries. Sets are appended to the end of the existing pending list in the order given.",
              items: Schema.object({
                properties: {
                  ActivityTypeID: Schema.string({
                    description:
                      "The ID of the activity type for this set. MUST exactly match an `ActivityTypeID` from the 'Available activities' list.",
                  }),
                  variables: Schema.array({
                    description:
                      "Per-variable measurements for this set. Each variable's `name` MUST match one of the variables defined for the chosen activity. Use null for `measurement` when the user did not provide a value.",
                    items: Schema.object({
                      properties: {
                        name: Schema.string({
                          description:
                            "Variable name (e.g. 'Weight', 'Reps', 'Distance', 'Time') - must match a variable defined for the chosen activity.",
                        }),
                        measurement: Schema.number({
                          description:
                            "The numeric value for this variable, or omit/null if the user did not give one.",
                        }),
                        unit: Schema.string({
                          description:
                            "The unit for the measurement (e.g. 'kg', 'lbs', 'reps', 'km', 'mi', 'min', 'sec'). Use the user's measurement system. Omit only if there is no sensible unit.",
                        }),
                      },
                      optionalProperties: ["measurement", "unit"],
                    }),
                  }),
                },
                optionalProperties: ["variables"],
              }),
            }),
          },
        }),
      },
      {
        name: "updateSet",
        description:
          "Replace a single set in the pending-sets list for a training log. Use this when the user corrects a set they've already described (e.g. 'no, the second set should have 12 reps'). `setNumber` is 1-based. The replacement must include the full set definition (ActivityTypeID + variables) - it does NOT merge with the existing set.",
        parameters: Schema.object({
          properties: {
            trainingLogID: Schema.string({
              description:
                "REQUIRED. The `logID` of the training log whose pending sets you want to update.",
            }),
            setNumber: Schema.integer({
              description:
                "1-based position of the set to replace in the current pending-sets list (1 = the first set). Must be between 1 and the current number of pending sets.",
            }),
            set: Schema.object({
              description:
                "The replacement set. ActivityTypeID MUST come from the 'Available activities' list.",
              properties: {
                ActivityTypeID: Schema.string({
                  description:
                    "The ID of the activity type for this set. MUST exactly match an `ActivityTypeID` from the 'Available activities' list.",
                }),
                variables: Schema.array({
                  description:
                    "Per-variable measurements for this set. Same rules as `addSets`.",
                  items: Schema.object({
                    properties: {
                      name: Schema.string({
                        description:
                          "Variable name - must match a variable defined for the chosen activity.",
                      }),
                      measurement: Schema.number({
                        description:
                          "The numeric value for this variable, or omit/null if unknown.",
                      }),
                      unit: Schema.string({
                        description:
                          "The unit for the measurement. Use the user's measurement system.",
                      }),
                    },
                    optionalProperties: ["measurement", "unit"],
                  }),
                }),
              },
              optionalProperties: ["variables"],
            }),
          },
        }),
      },
      {
        name: "removeSet",
        description:
          "Remove a single set from the pending-sets list for a training log. Use this when the user drops a set (e.g. 'scratch that third set' or 'remove the last one'). `setNumber` is 1-based; the remaining sets are automatically re-numbered.",
        parameters: Schema.object({
          properties: {
            trainingLogID: Schema.string({
              description:
                "REQUIRED. The `logID` of the training log whose pending set you want to remove.",
            }),
            setNumber: Schema.integer({
              description:
                "1-based position of the set to remove (1 = the first set). Must be between 1 and the current number of pending sets.",
            }),
          },
        }),
      },
      {
        name: "saveSession",
        description:
          "Persist the pending-sets list for a training log to the database as a Session, and link it to the training log. Only call this AFTER the user has explicitly confirmed the full pending-sets list is correct. The pending-sets list must be non-empty. Once saved the pending list is cleared and the session cannot be edited from this tool.",
        parameters: Schema.object({
          properties: {
            trainingLogID: Schema.string({
              description:
                "REQUIRED. The `logID` returned from the matching `addTraining` call earlier in this conversation.",
            }),
            name: Schema.string({
              description:
                "Optional short name for the session (e.g. 'Lower body strength', 'Threshold 4x8'). Omit if unsure.",
            }),
            description: Schema.string({
              description:
                "Optional longer description / notes about the session structure or how it went. Omit if there is nothing useful to add.",
            }),
            sessionTypeID: Schema.string({
              description:
                "Optional. ID of a session type from the 'Available session types' list. Omit if none clearly fits.",
            }),
          },
          optionalProperties: ["name", "description", "sessionTypeID"],
        }),
      },
    ],
  },
];

const isValidDateID = (value: unknown): value is string =>
  typeof value === "string" && /^\d{8}$/.test(value);

interface SavedLogBase {
  logID: string;
  dateID: string;
  savedAt: number;
}

type SavedLogEntry =
  | (SavedLogBase & { kind: "note"; log: NoteLog & { id: string } })
  | (SavedLogBase & { kind: "recovery"; log: RecoveryLog & { id: string } })
  | (SavedLogBase & { kind: "niggle"; log: NiggleLog & { id: string } })
  | (SavedLogBase & {
      kind: "performance";
      log: PerformanceLog & { id: string };
    })
  | (SavedLogBase & {
      kind: "training";
      log: TrainingLog & { id: string };
      linkedSession?: Session & { id: string };
    });

type LogKind = SavedLogEntry["kind"];

type EditingLog =
  | {
      kind: "note";
      logID: string;
      dateID: string;
      log: NoteLog & { id: string };
    }
  | {
      kind: "recovery";
      logID: string;
      dateID: string;
      log: RecoveryLog & { id: string };
    }
  | {
      kind: "niggle";
      logID: string;
      dateID: string;
      log: NiggleLog & { id: string };
    }
  | {
      kind: "performance";
      logID: string;
      dateID: string;
      log: PerformanceLog & { id: string };
    }
  | {
      kind: "training";
      logID: string;
      dateID: string;
      log: TrainingLog & { id: string };
    };

const formatDateIDLabel = (dateID: string, todayDateID: string): string => {
  if (!/^\d{8}$/.test(dateID)) return dateID;
  if (dateID === todayDateID) return "Today";
  const year = Number(dateID.slice(0, 4));
  const month = Number(dateID.slice(4, 6)) - 1;
  const day = Number(dateID.slice(6, 8));
  const date = new Date(year, month, day);
  if (Number.isNaN(date.getTime())) return dateID;
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

// Prebuilt Gemini voices. See: https://cloud.google.com/text-to-speech/docs/chirp3-hd
const VOICE_OPTIONS = [
  { id: "Aoede", label: "Aoede" },
  { id: "Charon", label: "Charon" },
  { id: "Fenrir", label: "Fenrir" },
  { id: "Kore", label: "Kore" },
  { id: "Puck", label: "Puck" },
];

const KIND_LABELS: Record<LogKind, string> = {
  note: "Note saved",
  recovery: "Recovery saved",
  niggle: "Niggle saved",
  performance: "Performance saved",
  training: "Training saved",
};

interface SavedLogCardProps {
  entry: SavedLogEntry;
  todayDateID: string;
  onEdit: (editing: EditingLog) => void;
  // Sets the AI has added in-memory but not yet committed to the database.
  // Only meaningful for `training` entries; ignored for other kinds.
  pendingSets?: PendingSet[];
  // Resolves an ActivityTypeID (on a saved ActivityMeasurement) to a
  // human-readable name. Required to render the saved `linkedSession`
  // via `SessionActivitiesTable`, which otherwise has no access to the
  // user's activity catalog.
  getActivityName: (measurement: ActivityMeasurement) => string;
}

// Convert a `PendingSet` (in-memory, not yet saved) to the
// `ActivityMeasurement` shape that `SessionActivitiesTable` expects, so we
// can share the same rendering for the pending and saved views. We
// deliberately leave `timeLogged` undefined - the table will render "-" in
// the Time column, making the "not saved yet" status visually obvious.
const pendingSetsToMeasurements = (
  pendingSets: PendingSet[],
): ActivityMeasurement[] =>
  pendingSets.map((set, index) => ({
    ActivityTypeID: set.ActivityTypeID,
    variables: set.variables.map((v) => ({
      name: v.name,
      measurement: v.measurement,
      ...(v.unit ? { unit: v.unit } : {}),
    })),
    activityIndex: index,
    ...(set.globalCategoryID ? { globalCategoryID: set.globalCategoryID } : {}),
  }));

const SavedLogCard: React.FC<SavedLogCardProps> = ({
  entry,
  todayDateID,
  onEdit,
  pendingSets,
  getActivityName,
}) => {
  const handleEdit = () => {
    if (entry.kind === "note") {
      onEdit({
        kind: "note",
        logID: entry.logID,
        dateID: entry.dateID,
        log: entry.log,
      });
    } else if (entry.kind === "recovery") {
      onEdit({
        kind: "recovery",
        logID: entry.logID,
        dateID: entry.dateID,
        log: entry.log,
      });
    } else if (entry.kind === "niggle") {
      onEdit({
        kind: "niggle",
        logID: entry.logID,
        dateID: entry.dateID,
        log: entry.log,
      });
    } else if (entry.kind === "performance") {
      onEdit({
        kind: "performance",
        logID: entry.logID,
        dateID: entry.dateID,
        log: entry.log,
      });
    } else if (entry.kind === "training") {
      onEdit({
        kind: "training",
        logID: entry.logID,
        dateID: entry.dateID,
        log: entry.log,
      });
    }
  };

  return (
    <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-2.5">
      <div className="flex items-center justify-between gap-2 mb-1.5 px-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
          <span className="text-[10px] uppercase tracking-wide text-green-700 dark:text-green-400 font-grotesk font-semibold truncate">
            {KIND_LABELS[entry.kind]}
          </span>
          <span className="text-[10px] text-base-content/50 font-grotesk tabular-nums">
            · {formatDateIDLabel(entry.dateID, todayDateID)}
          </span>
        </div>
        <button
          type="button"
          onClick={handleEdit}
          className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-grotesk text-base-content/70 hover:text-base-content hover:bg-base-200 transition-colors flex-shrink-0"
          aria-label={`Edit ${entry.kind} log`}
        >
          <PencilSquareIcon className="h-3 w-3" />
          Edit
        </button>
      </div>
      {entry.kind === "note" && <NoteLogCard log={entry.log} />}
      {entry.kind === "recovery" && <RecoveryLogCard log={entry.log} />}
      {entry.kind === "niggle" && <NiggleLogCard log={entry.log} />}
      {entry.kind === "performance" && <PerformanceLogCard log={entry.log} />}
      {entry.kind === "training" && (
        <TrainingLogCard log={entry.log} />
      )}
      {entry.kind === "training" &&
        entry.linkedSession &&
        ((entry.linkedSession.ActivityMeasurementList?.length ?? 0) > 0 ||
          (entry.linkedSession.activityBlocks || []).some(
            (b) => b.blockType === "notes" && (b.notesContent || "").trim(),
          )) && (
          <div className="mt-2 rounded-md border border-green-500/20 bg-base-200/60 p-2.5">
            <div className="flex items-center gap-1.5 mb-2 px-0.5">
              <span className="text-[10px] uppercase tracking-wide text-green-700 dark:text-green-400 font-grotesk font-semibold">
                Session saved
              </span>
              {entry.linkedSession.name && (
                <span className="text-[10px] text-base-content/60 font-grotesk truncate">
                  · {entry.linkedSession.name}
                </span>
              )}
              {(entry.linkedSession.ActivityMeasurementList?.length ?? 0) >
                0 && (
                <span className="text-[10px] text-base-content/50 font-grotesk tabular-nums ml-auto">
                  {entry.linkedSession.ActivityMeasurementList!.length} set
                  {entry.linkedSession.ActivityMeasurementList!.length === 1
                    ? ""
                    : "s"}
                </span>
              )}
            </div>
            <div className="space-y-2">
              <SessionNotes blocks={entry.linkedSession.activityBlocks} />
              {entry.linkedSession.ActivityMeasurementList &&
                entry.linkedSession.ActivityMeasurementList.length > 0 && (
                  <SessionActivitiesTable
                    measurements={entry.linkedSession.ActivityMeasurementList}
                    getActivityName={getActivityName}
                  />
                )}
            </div>
          </div>
        )}
      {entry.kind === "training" &&
        !entry.linkedSession &&
        pendingSets &&
        pendingSets.length > 0 && (
          <div className="mt-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5">
            <div className="flex items-center gap-1.5 mb-2 px-0.5">
              <span className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-400 font-grotesk font-semibold">
                Pending sets · not saved yet
              </span>
              <span className="text-[10px] text-base-content/50 font-grotesk tabular-nums ml-auto">
                {pendingSets.length} set{pendingSets.length === 1 ? "" : "s"}
              </span>
            </div>
            <SessionActivitiesTable
              measurements={pendingSetsToMeasurements(pendingSets)}
              getActivityName={(measurement) => {
                const match = pendingSets.find(
                  (s) => s.ActivityTypeID === measurement.ActivityTypeID,
                );
                return match?.activityName ?? getActivityName(measurement);
              }}
            />
          </div>
        )}
    </div>
  );
};

const OlyChat: React.FC<OlyChatProps> = ({ isOpen, onClose }) => {
  const { currentUser, userData } = useAuth();

  const [status, setStatus] = useState<CallStatus>("idle");
  // True while we're running a tool call on behalf of the model (e.g.
  // `getAthleteReport`, `addTraining`, etc.). Shown in
  // the status label as "Thinking…" so the user has a visual cue that Oly
  // is busy doing something rather than silently stuck. Tracked with a
  // small counter so overlapping function calls (rare, but possible) don't
  // flip the flag off prematurely - we only return to "Listening" once
  // every in-flight call has finished.
  const [pendingFunctionCalls, setPendingFunctionCalls] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [voiceName, setVoiceName] = useState<string>("Aoede");
  const [callDurationSec, setCallDurationSec] = useState<number>(0);
  const [savedLogs, setSavedLogs] = useState<SavedLogEntry[]>([]);
  const [editingLog, setEditingLog] = useState<EditingLog | null>(null);
  // Pending (not yet persisted) sets, keyed by `trainingLogID`. Populated by
  // the model via `addSets` / `updateSet` / `removeSet` and flushed to
  // Firestore (as a Session) by `saveSession`. Shown in the UI so the user
  // can visually confirm before the AI saves.
  const [pendingSetsByLog, setPendingSetsByLog] = useState<
    Record<string, PendingSet[]>
  >({});

  const sessionRef = useRef<LiveSession | null>(null);
  const controllerRef = useRef<AudioConversationController | null>(null);
  const callStartedAtRef = useRef<number | null>(null);
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const endCallRef = useRef<() => Promise<void>>(async () => {});
  // Mirror of `savedLogs` so the function-calling handler (closed over once
  // when the call starts) can read the latest entries - needed for the
  // pending-sets / saveSession tools to look up the training log by its ID.
  const savedLogsRef = useRef<SavedLogEntry[]>([]);
  useEffect(() => {
    savedLogsRef.current = savedLogs;
  }, [savedLogs]);
  // Mirror of `pendingSetsByLog` so the (closed-over) function-calling
  // handler can always read/write the latest pending sets without the extra
  // re-renders a useState dependency would force.
  const pendingSetsByLogRef = useRef<Record<string, PendingSet[]>>({});
  useEffect(() => {
    pendingSetsByLogRef.current = pendingSetsByLog;
  }, [pendingSetsByLog]);

  // Re-fetch a saved log from Firestore and update local state.
  // Used after edits so the displayed card reflects the persisted data.
  const refreshSavedLog = async (kind: LogKind, logID: string) => {
    if (!currentUser?.uid) return;
    const uid = currentUser.uid;
    try {
      let fetched:
        | (NoteLog & { id: string })
        | (RecoveryLog & { id: string })
        | (NiggleLog & { id: string })
        | (PerformanceLog & { id: string })
        | (TrainingLog & { id: string })
        | null = null;
      if (kind === "note") {
        const data = await getNoteLog(uid, logID);
        if (data) fetched = { ...data, id: logID };
      } else if (kind === "recovery") {
        const data = await getRecoveryLog(uid, logID);
        if (data) fetched = { ...data, id: logID };
      } else if (kind === "niggle") {
        const data = await getNiggleLog(uid, logID);
        if (data) fetched = { ...data, id: logID };
      } else if (kind === "performance") {
        const data = await getPerformanceLog(uid, logID);
        if (data) fetched = { ...data, id: logID };
      } else if (kind === "training") {
        const data = await getTrainingLog(uid, logID);
        if (data) fetched = { ...data, id: logID };
      }
      if (!fetched) return;
      const newLog = fetched;
      setSavedLogs((prev) =>
        prev.map((entry) =>
          entry.kind === kind && entry.logID === logID
            ? ({ ...entry, log: newLog } as SavedLogEntry)
            : entry,
        ),
      );
    } catch (err) {
      console.error(`Failed to refresh ${kind} log ${logID}:`, err);
    }
  };

  // Activity + session-type context. Loaded lazily the first time the model
  // calls `getActivitiesAndSessionTypes` (typically right before the
  // pending-sets workflow) so we don't pay the Firestore round-trip for
  // calls that never log session details. Cached for the rest of the call.
  const activityLookupRef = useRef<Map<string, ActivityLookupEntry>>(new Map());
  const sessionTypeLookupRef = useRef<Map<string, TrainingType>>(new Map());
  const activitiesPromiseRef = useRef<Promise<{
    activities: ActivityType[];
    sessionTypes: TrainingType[];
  }> | null>(null);

  // Athlete report cache. The model fetches this on demand via
  // `getAthleteReport` whenever the user asks about their past data, and we
  // reuse the same report for follow-up questions in the same call.
  const reportCacheRef = useRef<{
    report: string | null;
    summary: Record<string, unknown> | null;
    fetchedAt: number;
  } | null>(null);
  const reportPromiseRef = useRef<Promise<{
    report: string | null;
    summary: Record<string, unknown> | null;
  }> | null>(null);

  const isImperial = Boolean(userData?.isImperial);

  const loadCallContext = async (): Promise<{
    activities: ActivityType[];
    sessionTypes: TrainingType[];
  }> => {
    if (activitiesPromiseRef.current) return activitiesPromiseRef.current;

    const uid = currentUser?.uid;
    if (!uid) {
      activityLookupRef.current = new Map();
      sessionTypeLookupRef.current = new Map();
      return { activities: [], sessionTypes: [] };
    }

    const promise = (async () => {
      let activities: ActivityType[] = [];
      try {
        const userActivities = await activityService.fetchActivityTypes(uid);
        const packIds = userData?.adminActivityCatIDs || [];
        let packActivities: ActivityType[] = [];
        if (packIds.length > 0) {
          const globalTypes =
            await GlobalActivityService.fetchActivityTypesForCategories(
              packIds,
            );
          packActivities = globalTypes.map((gt) => ({
            id: gt.id,
            name: gt.name,
            description: gt.description,
            variables: gt.variables,
            image: gt.image,
            isFromPack: true,
            categoryId: gt.categoryId,
          }));
        }
        const userNames = new Set(
          userActivities.map((a) => a.name.toLowerCase()),
        );
        const uniquePackActivities = packActivities.filter(
          (pa) => !userNames.has(pa.name.toLowerCase()),
        );
        activities = [...userActivities, ...uniquePackActivities];
      } catch (loadError) {
        console.warn(
          "Failed to load activity types for Oly call context:",
          loadError,
        );
        activities = [];
      }

      let sessionTypes: TrainingType[] = [];
      try {
        sessionTypes = await trainingTypeService.fetchTrainingTypesForSports(
          userData?.trainingSportIDs,
          userData?.customTrainingTypes,
        );
      } catch (loadError) {
        console.warn(
          "Failed to load training types for Oly call context:",
          loadError,
        );
        sessionTypes = [];
      }

      activityLookupRef.current = buildActivityLookup(activities, isImperial);
      sessionTypeLookupRef.current = new Map(
        sessionTypes.filter((st) => st.id).map((st) => [st.id!, st]),
      );

      return { activities, sessionTypes };
    })();

    activitiesPromiseRef.current = promise;
    try {
      return await promise;
    } catch (err) {
      // Reset so a future call can retry.
      activitiesPromiseRef.current = null;
      throw err;
    }
  };

  // Lazily fetch the user's last 28 days of DayPosts and run the same
  // generator the DailyReport page uses. Cached per call.
  const loadAthleteReport = async (): Promise<{
    report: string | null;
    summary: Record<string, unknown> | null;
  }> => {
    if (reportCacheRef.current) {
      const { report, summary } = reportCacheRef.current;
      return { report, summary };
    }
    if (reportPromiseRef.current) return reportPromiseRef.current;

    const uid = currentUser?.uid;
    if (!uid) return { report: null, summary: null };

    const promise = (async () => {
      const dateIDs = getLast28Days();
      const dayPosts = await getDayPosts(uid, dateIDs);
      const sleepGoalHours = userData?.sleepGoal;
      const report = generateAthleteReport(
        dayPosts,
        /* simple */ false,
        /* recentAIInsights */ undefined,
        /* cyclePhase */ null,
        /* isFemale */ false,
        sleepGoalHours,
        /* excludeGarmin */ true,
      );

      const todayPost = dayPosts.find(
        (p) => p.dateID === generateDateID(new Date()),
      );
      const peak = todayPost?.peakScoreLog;
      const wellness = todayPost?.wellnessLogs?.[0];
      const summary: Record<string, unknown> = {
        todayDateID: generateDateID(new Date()),
        peakScore: peak?.peakScore ?? null,
        hlr: peak?.hlr ?? null,
        wellnessFinal: peak?.wellnessFinal ?? null,
        workloadFactor: peak?.workloadFactor ?? null,
        sickFactor: peak?.sickFactor ?? null,
        sleepLastNight: wellness?.sleep ?? null,
        recoveryToday: wellness?.recovery ?? null,
        sorenessToday: wellness?.soreness ?? null,
        moodToday: wellness?.mood ?? null,
        stressToday: wellness?.stress ?? null,
        healthToday: wellness?.health ?? null,
        injuredToday: wellness?.injured ?? false,
        niggleCountToday: todayPost?.niggleLogs?.length ?? 0,
        sleepGoalHours: sleepGoalHours ?? null,
      };

      reportCacheRef.current = {
        report,
        summary,
        fetchedAt: Date.now(),
      };
      return { report, summary };
    })();

    reportPromiseRef.current = promise;
    try {
      return await promise;
    } catch (err) {
      reportPromiseRef.current = null;
      throw err;
    } finally {
      reportPromiseRef.current = null;
    }
  };

  const stopDurationTimer = () => {
    if (durationTimerRef.current !== null) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
  };

  const teardown = async () => {
    stopDurationTimer();
    callStartedAtRef.current = null;
    try {
      await controllerRef.current?.stop();
    } catch (stopError) {
      console.warn("Error stopping audio controller:", stopError);
    }
    controllerRef.current = null;
    try {
      await sessionRef.current?.close();
    } catch (closeError) {
      console.warn("Error closing live session:", closeError);
    }
    sessionRef.current = null;
  };

  // Inner handler that does the actual tool-call dispatch. Wrapped by
  // `handleFunctionCalls` below so we can toggle the "Thinking…" status
  // label around every invocation regardless of which branch returns.
  const runFunctionCall = async (
    calls: FunctionCall[],
  ): Promise<FunctionResponse> => {
    // The SDK currently expects a single FunctionResponse back per tool-call
    // message, so we handle the first call and acknowledge it. Add more
    // function declarations to OLY_TOOLS to support additional in-app actions.
    const call = calls[0];

    if (call?.name === "endCall") {
      // Defer the actual hangup briefly so the FunctionResponse is sent and
      // any short farewell audio from the model has time to play out.
      setTimeout(() => {
        void endCallRef.current();
      }, 800);

      return {
        id: call.id,
        name: call.name,
        response: { success: true, message: "Ending the call now." },
      };
    }

    if (call?.name === "getAthleteReport") {
      const uid = currentUser?.uid;
      if (!uid) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: "No signed-in user; cannot fetch athlete report.",
          },
        };
      }

      try {
        const { report, summary } = await loadAthleteReport();
        if (!report) {
          return {
            id: call.id,
            name: call.name,
            response: {
              success: true,
              hasData: false,
              summary,
              message:
                "No Peak Score for today yet, so there isn't a full report available. Tell the user they need to log today's wellness/training to get a fresh report.",
            },
          };
        }
        return {
          id: call.id,
          name: call.name,
          response: {
            success: true,
            hasData: true,
            summary,
            report,
            cached: reportCacheRef.current
              ? Date.now() - reportCacheRef.current.fetchedAt < 1000
                ? false
                : true
              : false,
          },
        };
      } catch (err: any) {
        console.error("Failed to load athlete report from Oly:", err);
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: err?.message ?? "Unknown error loading athlete report.",
          },
        };
      }
    }

    if (call?.name === "getActivitiesAndSessionTypes") {
      const uid = currentUser?.uid;
      if (!uid) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "No signed-in user; cannot fetch activities or session types.",
          },
        };
      }

      try {
        const { activities, sessionTypes } = await loadCallContext();

        const activitiesForPrompt = activities
          .filter((a) => a.id)
          .map((a) => ({
            ActivityTypeID: a.id!,
            name: a.name,
            ...(a.isFromPack && a.categoryId
              ? { globalCategoryID: a.categoryId }
              : {}),
            variables: normalizeActivityVariables(a.variables, isImperial).map(
              (v) => ({
                name: v.name,
                defaultUnit: v.defaultUnit || undefined,
              }),
            ),
          }));
        const sessionTypesForPrompt = sessionTypes
          .filter((st) => st.id)
          .map((st) => ({ id: st.id, name: st.name }));

        return {
          id: call.id,
          name: call.name,
          response: {
            success: true,
            activities: activitiesForPrompt,
            sessionTypes: sessionTypesForPrompt,
            measurementSystem: isImperial ? "Imperial" : "Metric",
            message:
              activitiesForPrompt.length === 0
                ? "The user has no activities set up. Tell them session details cannot be added until they create activities in the app."
                : `Loaded ${activitiesForPrompt.length} activit${
                    activitiesForPrompt.length === 1 ? "y" : "ies"
                  } and ${sessionTypesForPrompt.length} session type${
                    sessionTypesForPrompt.length === 1 ? "" : "s"
                  }. Use ONLY these ActivityTypeID values when calling addSets / updateSet; one entry per set.`,
          },
        };
      } catch (err: any) {
        console.error("Failed to load activities/session types from Oly:", err);
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              err?.message ?? "Unknown error loading activities/session types.",
          },
        };
      }
    }

    if (call?.name === "addNote") {
      const args = (call.args ?? {}) as {
        notes?: unknown;
        dateID?: unknown;
      };
      const notes = typeof args.notes === "string" ? args.notes.trim() : "";
      const requestedDateID = args.dateID;
      const uid = currentUser?.uid;

      if (!uid) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: "No signed-in user; cannot save note.",
          },
        };
      }

      if (!notes) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: "Missing note content. Ask the user what they want to save.",
          },
        };
      }

      const dateID =
        requestedDateID === undefined || requestedDateID === ""
          ? generateDateID(new Date())
          : isValidDateID(requestedDateID)
            ? requestedDateID
            : null;

      if (dateID === null) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Invalid dateID. It must be 8 digits in YYYYMMDD format. Ask the user to clarify the date.",
          },
        };
      }

      try {
        const logID = await addNoteLog(uid, { notes }, dateID);
        await addLogIDToDayPost(uid, dateID, logID, "noteLogIDs");

        const now = Date.now();
        const newLog: NoteLog & { id: string } = {
          id: logID,
          dateID,
          notes,
          timestamp: now,
        };
        setSavedLogs((prev) => [
          ...prev,
          { kind: "note", logID, dateID, savedAt: now, log: newLog },
        ]);

        return {
          id: call.id,
          name: call.name,
          response: {
            success: true,
            logID,
            dateID,
            message: `Saved note (${notes.length} chars) to ${dateID}.`,
          },
        };
      } catch (err: any) {
        console.error("Failed to save note from Oly:", err);
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: err?.message ?? "Unknown error saving note.",
          },
        };
      }
    }

    if (call?.name === "addRecovery") {
      const args = (call.args ?? {}) as {
        selectedRecovery?: unknown;
        notes?: unknown;
        dateID?: unknown;
      };
      const uid = currentUser?.uid;

      if (!uid) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: "No signed-in user; cannot save recovery log.",
          },
        };
      }

      const rawMethods = Array.isArray(args.selectedRecovery)
        ? args.selectedRecovery
        : [];
      const resolvedMethods: string[] = [];
      const unknownMethods: string[] = [];
      for (const value of rawMethods) {
        const resolved = resolveRecoveryMethod(value);
        if (resolved) {
          if (!resolvedMethods.includes(resolved))
            resolvedMethods.push(resolved);
        } else if (typeof value === "string") {
          unknownMethods.push(value);
        }
      }

      if (resolvedMethods.length === 0) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: unknownMethods.length
              ? `None of the provided methods are valid: ${unknownMethods.join(", ")}. Allowed methods: ${RECOVERY_METHOD_NAMES.join(", ")}.`
              : "Provide at least one recovery method from the allowed list.",
          },
        };
      }

      const notes = typeof args.notes === "string" ? args.notes.trim() : "";

      const requestedDateID = args.dateID;
      const dateID =
        requestedDateID === undefined || requestedDateID === ""
          ? generateDateID(new Date())
          : isValidDateID(requestedDateID)
            ? requestedDateID
            : null;

      if (dateID === null) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Invalid dateID. It must be 8 digits in YYYYMMDD format. Ask the user to clarify the date.",
          },
        };
      }

      const pointsAmount = calculateRecoveryPoints(resolvedMethods);

      try {
        const logID = await addRecoveryLog(
          uid,
          { selectedRecovery: resolvedMethods, notes, pointsAmount },
          dateID,
        );
        await addLogIDToDayPost(uid, dateID, logID, "recoveryLogIDs");

        const now = Date.now();
        const newLog: RecoveryLog & { id: string } = {
          id: logID,
          dateID,
          notes,
          timestamp: now,
          selectedRecovery: resolvedMethods,
          pointsAmount,
        };
        setSavedLogs((prev) => [
          ...prev,
          {
            kind: "recovery",
            logID,
            dateID,
            savedAt: now,
            log: newLog,
          },
        ]);

        return {
          id: call.id,
          name: call.name,
          response: {
            success: true,
            logID,
            dateID,
            pointsAmount,
            selectedRecovery: resolvedMethods,
            ignoredMethods: unknownMethods,
            message: `Saved recovery log (${resolvedMethods.length} method${resolvedMethods.length === 1 ? "" : "s"}, ${pointsAmount} pts) to ${dateID}.`,
          },
        };
      } catch (err: any) {
        console.error("Failed to save recovery log from Oly:", err);
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: err?.message ?? "Unknown error saving recovery log.",
          },
        };
      }
    }

    if (call?.name === "addNiggle") {
      const args = (call.args ?? {}) as {
        pain?: unknown;
        impact?: unknown;
        notes?: unknown;
        dateID?: unknown;
      };
      const uid = currentUser?.uid;

      if (!uid) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: "No signed-in user; cannot save niggle log.",
          },
        };
      }

      const pain = coerceNiggleScale(args.pain);
      const impact = coerceNiggleScale(args.impact);

      if (pain === null) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Missing or invalid `pain`. Provide an integer from 1 (no pain) to 10 (unbearable).",
          },
        };
      }
      if (impact === null) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Missing or invalid `impact`. Provide an integer from 1 (no impact) to 10 (cannot perform at all).",
          },
        };
      }

      const notes = typeof args.notes === "string" ? args.notes.trim() : "";
      if (!notes) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Missing `notes`. Ask the user where the niggle is and what it feels like before saving.",
          },
        };
      }

      const requestedDateID = args.dateID;
      const dateID =
        requestedDateID === undefined || requestedDateID === ""
          ? generateDateID(new Date())
          : isValidDateID(requestedDateID)
            ? requestedDateID
            : null;

      if (dateID === null) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Invalid dateID. It must be 8 digits in YYYYMMDD format. Ask the user to clarify the date.",
          },
        };
      }

      try {
        const logID = await addNiggleLog(uid, { pain, impact, notes }, dateID);
        await addLogIDToDayPost(uid, dateID, logID, "niggleLogIDs");

        const now = Date.now();
        const newLog: NiggleLog & { id: string } = {
          id: logID,
          dateID,
          pain,
          impact,
          notes,
          timestamp: now,
        };
        setSavedLogs((prev) => [
          ...prev,
          {
            kind: "niggle",
            logID,
            dateID,
            savedAt: now,
            log: newLog,
          },
        ]);

        return {
          id: call.id,
          name: call.name,
          response: {
            success: true,
            logID,
            dateID,
            pain,
            impact,
            message: `Saved niggle (pain ${pain}/10, impact ${impact}/10) to ${dateID}.`,
          },
        };
      } catch (err: any) {
        console.error("Failed to save niggle log from Oly:", err);
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: err?.message ?? "Unknown error saving niggle log.",
          },
        };
      }
    }

    if (call?.name === "addPerformance") {
      const args = (call.args ?? {}) as {
        performanceTitle?: unknown;
        performanceResult?: unknown;
        performanceExpectation?: unknown;
        notes?: unknown;
        tag?: unknown;
        dateID?: unknown;
      };
      const uid = currentUser?.uid;

      if (!uid) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: "No signed-in user; cannot save performance log.",
          },
        };
      }

      const performanceTitle =
        typeof args.performanceTitle === "string"
          ? args.performanceTitle.trim()
          : "";
      if (!performanceTitle) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Missing `performanceTitle`. Ask the user what to call the performance before saving.",
          },
        };
      }

      const performanceResult =
        typeof args.performanceResult === "string"
          ? args.performanceResult.trim()
          : "";
      if (!performanceResult) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Missing `performanceResult`. Ask the user for the actual result (time, distance, score, etc.) before saving.",
          },
        };
      }

      const expectationRaw =
        typeof args.performanceExpectation === "number"
          ? args.performanceExpectation
          : Number(args.performanceExpectation);
      const performanceExpectation = Number.isFinite(expectationRaw)
        ? Math.round(expectationRaw)
        : NaN;
      if (
        !Number.isFinite(performanceExpectation) ||
        performanceExpectation < 1 ||
        performanceExpectation > 10
      ) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Missing or invalid `performanceExpectation`. Provide an integer from 1 (far below expectations) to 10 (personal best).",
          },
        };
      }

      const notes = typeof args.notes === "string" ? args.notes.trim() : "";

      const tagRaw = Array.isArray(args.tag) ? args.tag : [];
      const tag: string[] = [];
      const invalidTags: string[] = [];
      for (const value of tagRaw) {
        const resolved = resolvePerformanceTag(value);
        if (resolved) {
          if (!tag.includes(resolved)) tag.push(resolved);
        } else if (typeof value === "string" && value.trim()) {
          invalidTags.push(value);
        }
      }
      if (invalidTags.length > 0) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: `Invalid tag(s): ${invalidTags.join(
              ", ",
            )}. Allowed values are ${PERFORMANCE_TAG_CODES.join(
              ", ",
            )}. Drop any tags that don't apply.`,
          },
        };
      }

      const requestedDateID = args.dateID;
      const dateID =
        requestedDateID === undefined || requestedDateID === ""
          ? generateDateID(new Date())
          : isValidDateID(requestedDateID)
            ? requestedDateID
            : null;

      if (dateID === null) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Invalid dateID. It must be 8 digits in YYYYMMDD format. Ask the user to clarify the date.",
          },
        };
      }

      try {
        const logID = await addPerformanceLog(
          uid,
          {
            performanceTitle,
            performanceResult,
            performanceExpectation,
            notes,
            performanceDate: dateID,
            ...(tag.length > 0 && { tag }),
          },
          dateID,
        );
        await addLogIDToDayPost(uid, dateID, logID, "performanceLogIDs");

        const now = Date.now();
        const newLog: PerformanceLog & { id: string } = {
          id: logID,
          dateID,
          performanceTitle,
          performanceResult,
          performanceExpectation,
          notes,
          performanceDate: dateID,
          timestamp: now,
          ...(tag.length > 0 && { tag }),
        };
        setSavedLogs((prev) => [
          ...prev,
          {
            kind: "performance",
            logID,
            dateID,
            savedAt: now,
            log: newLog,
          },
        ]);

        return {
          id: call.id,
          name: call.name,
          response: {
            success: true,
            logID,
            dateID,
            performanceTitle,
            performanceResult,
            performanceExpectation,
            tag,
            message: `Saved performance "${performanceTitle}" (${performanceResult}, ${performanceExpectation}/10${
              tag.length > 0 ? `, ${tag.join("/")}` : ""
            }) to ${dateID}.`,
          },
        };
      } catch (err: any) {
        console.error("Failed to save performance log from Oly:", err);
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: err?.message ?? "Unknown error saving performance log.",
          },
        };
      }
    }

    if (call?.name === "addTraining") {
      const args = (call.args ?? {}) as {
        description?: unknown;
        duration?: unknown;
        muscular?: unknown;
        respiratory?: unknown;
        trainingPerformed?: unknown;
        notes?: unknown;
        pain?: unknown;
        injured?: unknown;
        dateID?: unknown;
      };
      const uid = currentUser?.uid;

      if (!uid) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: "No signed-in user; cannot save training log.",
          },
        };
      }

      const description =
        typeof args.description === "string" ? args.description.trim() : "";
      if (!description) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Missing `description`. Ask the user what kind of session it was (e.g. 'easy run', 'threshold', 'strength') before saving.",
          },
        };
      }

      const durationRaw =
        typeof args.duration === "number"
          ? args.duration
          : Number(args.duration);
      const duration = Number.isFinite(durationRaw)
        ? Math.round(durationRaw)
        : NaN;
      if (!Number.isFinite(duration) || duration <= 0 || duration > 24 * 60) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Missing or invalid `duration`. Provide the session length in MINUTES as a positive integer (e.g. 30, 60, 90).",
          },
        };
      }

      const muscular = coerceTrainingScale(args.muscular);
      if (muscular === null) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Missing or invalid `muscular`. Provide an integer from 1 (very little muscle effort) to 10 (maximal effort, failure).",
          },
        };
      }

      const respiratory = coerceTrainingScale(args.respiratory);
      if (respiratory === null) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Missing or invalid `respiratory`. Provide an integer from 1 (easy at rest) to 10 (maximal breathlessness).",
          },
        };
      }

      const rpe = (muscular + respiratory) / 2;

      // Resolve the description to a training type (via name / alternative
      // names) so the log carries a trainingTypeID and its load modifier.
      let matchedTrainingType: TrainingType | null = null;
      try {
        matchedTrainingType =
          await trainingTypeService.findTrainingTypeByName(description);
      } catch (err) {
        console.error("Failed to resolve training type from description:", err);
      }
      const loadModifier = matchedTrainingType?.modifier ?? 1;
      const load = Math.round(rpe * duration * loadModifier);

      let trainingPerformed: number | undefined;
      if (
        args.trainingPerformed !== undefined &&
        args.trainingPerformed !== null
      ) {
        const tp = coerceTrainingScale(args.trainingPerformed);
        if (tp === null) {
          return {
            id: call.id,
            name: call.name,
            response: {
              success: false,
              error:
                "Invalid `trainingPerformed`. Provide an integer from 1 (barely got through it) to 10 (nailed it), or omit for sessions below RPE 7.5.",
            },
          };
        }
        trainingPerformed = tp;
      }

      const notes = typeof args.notes === "string" ? args.notes.trim() : "";
      const pain = typeof args.pain === "boolean" ? args.pain : undefined;
      const injured =
        typeof args.injured === "boolean" ? args.injured : undefined;

      const requestedDateID = args.dateID;
      const dateID =
        requestedDateID === undefined || requestedDateID === ""
          ? generateDateID(new Date())
          : isValidDateID(requestedDateID)
            ? requestedDateID
            : null;

      if (dateID === null) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Invalid dateID. It must be 8 digits in YYYYMMDD format. Ask the user to clarify the date.",
          },
        };
      }

      try {
        // Prefer the canonical training type name for the tag when resolved.
        const tag = [matchedTrainingType?.name ?? description];
        const trainingTypeID = matchedTrainingType?.id;
        const logID = await addTrainingLog(
          uid,
          {
            tag,
            ...(trainingTypeID ? { trainingTypeID } : {}),
            duration,
            muscular,
            respiratory,
            rpe,
            load,
            notes,
            ...(trainingPerformed !== undefined && { trainingPerformed }),
            ...(pain !== undefined && { pain }),
            ...(injured !== undefined && { injured }),
          },
          dateID,
        );
        await addLogIDToDayPost(uid, dateID, logID, "trainingLogIDs");

        const now = Date.now();
        const newLog: TrainingLog & { id: string } = {
          id: logID,
          dateID,
          tag,
          ...(trainingTypeID ? { trainingTypeID } : {}),
          duration,
          muscular,
          respiratory,
          rpe,
          load,
          notes,
          timestamp: now,
          ...(trainingPerformed !== undefined && { trainingPerformed }),
          ...(pain !== undefined && { pain }),
          ...(injured !== undefined && { injured }),
        };
        setSavedLogs((prev) => [
          ...prev,
          {
            kind: "training",
            logID,
            dateID,
            savedAt: now,
            log: newLog,
          },
        ]);

        return {
          id: call.id,
          name: call.name,
          response: {
            success: true,
            logID,
            dateID,
            description,
            duration,
            muscular,
            respiratory,
            rpe,
            load,
            ...(trainingPerformed !== undefined && { trainingPerformed }),
            message: `Saved training "${description}" (${duration} min, RPE ${rpe.toFixed(
              1,
            )}, load ${load}) to ${dateID}.`,
          },
        };
      } catch (err: any) {
        console.error("Failed to save training log from Oly:", err);
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: err?.message ?? "Unknown error saving training log.",
          },
        };
      }
    }

    // Shared lookup for the pending-sets tools. Validates the trainingLogID
    // against a saved training log in this call, ensures the activity lookup
    // is loaded, and returns either a ready-to-use context or an early
    // FunctionResponse carrying an error the tool can bail out with.
    const resolvePendingSetsContext = async (
      trainingLogIDInput: unknown,
    ): Promise<
      | {
          ok: true;
          trainingLogID: string;
          matchingTraining: Extract<SavedLogEntry, { kind: "training" }>;
          activityLookup: Map<string, ActivityLookupEntry>;
        }
      | { ok: false; response: FunctionResponse }
    > => {
      const trainingLogID =
        typeof trainingLogIDInput === "string" ? trainingLogIDInput.trim() : "";
      if (!trainingLogID) {
        return {
          ok: false,
          response: {
            id: call.id,
            name: call.name,
            response: {
              success: false,
              error:
                "Missing `trainingLogID`. You must call `addTraining` first and pass the returned `logID` here.",
            },
          },
        };
      }

      const matchingTraining = savedLogsRef.current.find(
        (l) => l.kind === "training" && l.logID === trainingLogID,
      ) as Extract<SavedLogEntry, { kind: "training" }> | undefined;
      if (!matchingTraining) {
        return {
          ok: false,
          response: {
            id: call.id,
            name: call.name,
            response: {
              success: false,
              error:
                "Unknown `trainingLogID`. It must match the `logID` returned from a successful `addTraining` call earlier in this conversation.",
            },
          },
        };
      }
      if (matchingTraining.linkedSession) {
        return {
          ok: false,
          response: {
            id: call.id,
            name: call.name,
            response: {
              success: false,
              error:
                "This training log already has session details attached. Tell the user it's already been saved and cannot be edited from here.",
            },
          },
        };
      }

      if (activityLookupRef.current.size === 0) {
        try {
          await loadCallContext();
        } catch (err) {
          console.warn(
            "Failed to load activities lazily during pending-sets call:",
            err,
          );
        }
      }
      const activityLookup = activityLookupRef.current;
      if (activityLookup.size === 0) {
        return {
          ok: false,
          response: {
            id: call.id,
            name: call.name,
            response: {
              success: false,
              error:
                "No activity types are available. Either the user has no activities set up (tell them they need to create activities in the app first), or you skipped `getActivitiesAndSessionTypes` - call that tool first to discover the available ActivityTypeIDs and try again.",
            },
          },
        };
      }

      return { ok: true, trainingLogID, matchingTraining, activityLookup };
    };

    if (call?.name === "addSets") {
      if (!currentUser?.uid) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: "No signed-in user; cannot update pending sets.",
          },
        };
      }

      const args = (call.args ?? {}) as {
        trainingLogID?: unknown;
        sets?: unknown;
      };

      const ctx = await resolvePendingSetsContext(args.trainingLogID);
      if (!ctx.ok) return ctx.response;
      const { trainingLogID, activityLookup } = ctx;

      const rawSets = Array.isArray(args.sets) ? args.sets : [];
      if (rawSets.length === 0) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "Missing `sets`. Provide at least one set entry (one entry per set).",
          },
        };
      }

      const normalizedSets: PendingSet[] = [];
      const unknownActivityIDs: string[] = [];
      const ignoredVariables: string[] = [];
      const perEntryErrors: string[] = [];
      for (let i = 0; i < rawSets.length; i++) {
        const result = normalizePendingSet(rawSets[i], activityLookup);
        if (!result.ok) {
          perEntryErrors.push(`Entry ${i + 1}: ${result.error}`);
          if (
            result.unknownActivityID &&
            !unknownActivityIDs.includes(result.unknownActivityID)
          ) {
            unknownActivityIDs.push(result.unknownActivityID);
          }
          continue;
        }
        for (const v of result.ignoredVariables) {
          if (!ignoredVariables.includes(v)) ignoredVariables.push(v);
        }
        normalizedSets.push(result.set);
      }

      if (normalizedSets.length === 0) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              unknownActivityIDs.length > 0
                ? `None of the provided ActivityTypeID values matched the user's available activities. Unknown: ${unknownActivityIDs.join(", ")}. Use IDs from the 'Available activities' list.`
                : perEntryErrors.join(" ") || "No valid sets were provided.",
          },
        };
      }

      const previous = pendingSetsByLogRef.current[trainingLogID] ?? [];
      const updated = [...previous, ...normalizedSets];
      setPendingSetsByLog((prev) => ({
        ...prev,
        [trainingLogID]: updated,
      }));
      pendingSetsByLogRef.current = {
        ...pendingSetsByLogRef.current,
        [trainingLogID]: updated,
      };

      const addedCount = normalizedSets.length;
      return {
        id: call.id,
        name: call.name,
        response: {
          success: true,
          trainingLogID,
          addedCount,
          totalPendingSets: updated.length,
          pendingSets: updated.map((s, i) => ({
            setNumber: i + 1,
            ActivityTypeID: s.ActivityTypeID,
            activityName: s.activityName,
            variables: s.variables,
          })),
          ignoredActivityIDs: unknownActivityIDs,
          ignoredVariables,
          perEntryErrors,
          message: `Added ${addedCount} set${
            addedCount === 1 ? "" : "s"
          }. Pending list now has ${updated.length} set${
            updated.length === 1 ? "" : "s"
          }:\n${describePendingSets(updated)}\nNothing is saved yet - call \`saveSession\` once the user confirms.`,
        },
      };
    }

    if (call?.name === "updateSet") {
      if (!currentUser?.uid) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: "No signed-in user; cannot update pending sets.",
          },
        };
      }

      const args = (call.args ?? {}) as {
        trainingLogID?: unknown;
        setNumber?: unknown;
        set?: unknown;
      };

      const ctx = await resolvePendingSetsContext(args.trainingLogID);
      if (!ctx.ok) return ctx.response;
      const { trainingLogID, activityLookup } = ctx;

      const current = pendingSetsByLogRef.current[trainingLogID] ?? [];
      if (current.length === 0) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "There are no pending sets for this training log yet. Call `addSets` first.",
          },
        };
      }

      const setNumberRaw =
        typeof args.setNumber === "number"
          ? args.setNumber
          : Number(args.setNumber);
      if (
        !Number.isFinite(setNumberRaw) ||
        !Number.isInteger(setNumberRaw) ||
        setNumberRaw < 1 ||
        setNumberRaw > current.length
      ) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: `Invalid \`setNumber\` ${args.setNumber}. Must be an integer between 1 and ${current.length}.`,
          },
        };
      }

      const normalized = normalizePendingSet(args.set, activityLookup);
      if (!normalized.ok) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: normalized.error,
          },
        };
      }

      const setNumber = setNumberRaw;
      const updated = current.slice();
      updated[setNumber - 1] = normalized.set;

      setPendingSetsByLog((prev) => ({
        ...prev,
        [trainingLogID]: updated,
      }));
      pendingSetsByLogRef.current = {
        ...pendingSetsByLogRef.current,
        [trainingLogID]: updated,
      };

      return {
        id: call.id,
        name: call.name,
        response: {
          success: true,
          trainingLogID,
          setNumber,
          totalPendingSets: updated.length,
          pendingSets: updated.map((s, i) => ({
            setNumber: i + 1,
            ActivityTypeID: s.ActivityTypeID,
            activityName: s.activityName,
            variables: s.variables,
          })),
          ignoredVariables: normalized.ignoredVariables,
          message: `Updated set ${setNumber}. Pending list now:\n${describePendingSets(updated)}`,
        },
      };
    }

    if (call?.name === "removeSet") {
      if (!currentUser?.uid) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: "No signed-in user; cannot update pending sets.",
          },
        };
      }

      const args = (call.args ?? {}) as {
        trainingLogID?: unknown;
        setNumber?: unknown;
      };

      const ctx = await resolvePendingSetsContext(args.trainingLogID);
      if (!ctx.ok) return ctx.response;
      const { trainingLogID } = ctx;

      const current = pendingSetsByLogRef.current[trainingLogID] ?? [];
      if (current.length === 0) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "There are no pending sets for this training log yet - nothing to remove.",
          },
        };
      }

      const setNumberRaw =
        typeof args.setNumber === "number"
          ? args.setNumber
          : Number(args.setNumber);
      if (
        !Number.isFinite(setNumberRaw) ||
        !Number.isInteger(setNumberRaw) ||
        setNumberRaw < 1 ||
        setNumberRaw > current.length
      ) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: `Invalid \`setNumber\` ${args.setNumber}. Must be an integer between 1 and ${current.length}.`,
          },
        };
      }

      const setNumber = setNumberRaw;
      const removed = current[setNumber - 1];
      const updated = current.slice();
      updated.splice(setNumber - 1, 1);

      setPendingSetsByLog((prev) => {
        const next = { ...prev };
        if (updated.length === 0) {
          delete next[trainingLogID];
        } else {
          next[trainingLogID] = updated;
        }
        return next;
      });
      {
        const nextRef = { ...pendingSetsByLogRef.current };
        if (updated.length === 0) {
          delete nextRef[trainingLogID];
        } else {
          nextRef[trainingLogID] = updated;
        }
        pendingSetsByLogRef.current = nextRef;
      }

      return {
        id: call.id,
        name: call.name,
        response: {
          success: true,
          trainingLogID,
          removedSetNumber: setNumber,
          removed: {
            ActivityTypeID: removed.ActivityTypeID,
            activityName: removed.activityName,
            variables: removed.variables,
          },
          totalPendingSets: updated.length,
          pendingSets: updated.map((s, i) => ({
            setNumber: i + 1,
            ActivityTypeID: s.ActivityTypeID,
            activityName: s.activityName,
            variables: s.variables,
          })),
          message:
            updated.length === 0
              ? `Removed set ${setNumber} (${describePendingSet(removed)}). Pending list is now empty.`
              : `Removed set ${setNumber} (${describePendingSet(removed)}). Pending list now:\n${describePendingSets(updated)}`,
        },
      };
    }

    if (call?.name === "saveSession") {
      const uid = currentUser?.uid;
      if (!uid) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: "No signed-in user; cannot save session.",
          },
        };
      }

      const args = (call.args ?? {}) as {
        trainingLogID?: unknown;
        name?: unknown;
        description?: unknown;
        sessionTypeID?: unknown;
      };

      const ctx = await resolvePendingSetsContext(args.trainingLogID);
      if (!ctx.ok) return ctx.response;
      const { trainingLogID, matchingTraining } = ctx;

      const pendingSets = pendingSetsByLogRef.current[trainingLogID] ?? [];
      if (pendingSets.length === 0) {
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error:
              "No pending sets to save for this training log. Call `addSets` to build the list first.",
          },
        };
      }

      const measurementList: ActivityMeasurement[] = pendingSets.map(
        (s, i) => ({
          ActivityTypeID: s.ActivityTypeID,
          variables: s.variables.map((v) => ({
            name: v.name,
            measurement: v.measurement,
            ...(v.unit ? { unit: v.unit } : {}),
          })),
          activityIndex: i,
          ...(s.globalCategoryID
            ? { globalCategoryID: s.globalCategoryID }
            : {}),
        }),
      );

      const trainingDescription = matchingTraining.log.tag?.[0] ?? "Training";
      const sessionName =
        typeof args.name === "string" && args.name.trim()
          ? args.name.trim()
          : trainingDescription;
      const sessionDescription =
        typeof args.description === "string" && args.description.trim()
          ? args.description.trim()
          : matchingTraining.log.notes || trainingDescription;

      // The model-facing param is still called sessionTypeID, but the IDs it
      // gets from getActivitiesAndSessionTypes are trainingTypes doc IDs.
      const sessionTypeIDInput =
        typeof args.sessionTypeID === "string" ? args.sessionTypeID.trim() : "";
      let trainingTypeID: string | undefined;
      let invalidSessionType: string | undefined;
      if (sessionTypeIDInput) {
        const stMatch = sessionTypeLookupRef.current.get(sessionTypeIDInput);
        if (stMatch) {
          trainingTypeID = sessionTypeIDInput;
        } else {
          invalidSessionType = sessionTypeIDInput;
        }
      }

      // Convert YYYYMMDD -> YYYY-MM-DD for sessionService.createSession.
      const dateID = matchingTraining.dateID;
      const plannedDate = `${dateID.slice(0, 4)}-${dateID.slice(4, 6)}-${dateID.slice(6, 8)}`;

      try {
        const sessionID = await sessionService.createSession(uid, {
          ActivityMeasurementList: measurementList,
          plannedDate,
          name: sessionName,
          description: sessionDescription,
          status: "complete",
          linkedTrainingLogID: trainingLogID,
          ...(trainingTypeID ? { trainingTypeID } : {}),
        });

        // sessionService.createSession already links the session to the
        // training log when linkedTrainingLogID is provided, but we still
        // call linkSessionToLog defensively (idempotent) so the link is
        // guaranteed even if the auto-link path errored.
        try {
          await sessionService.linkSessionToLog(
            uid,
            sessionID,
            trainingLogID,
            dateID,
          );
        } catch (linkError) {
          console.warn(
            "Failed to back-link session on training log:",
            linkError,
          );
        }

        const linkedSession: Session & { id: string } = {
          id: sessionID,
          name: sessionName,
          description: sessionDescription,
          plannedDate,
          status: "complete",
          ActivityMeasurementList: measurementList,
          linkedTrainingLogID: trainingLogID,
          ...(trainingTypeID ? { trainingTypeID } : {}),
        };

        setSavedLogs((prev) =>
          prev.map((entry) =>
            entry.kind === "training" && entry.logID === trainingLogID
              ? {
                  ...entry,
                  linkedSession,
                  log: {
                    ...entry.log,
                    linkedSessionIDs: [sessionID],
                    linkedSessionID: sessionID,
                  },
                }
              : entry,
          ),
        );

        // Clear the pending sets for this training log now that we've
        // flushed them to Firestore.
        setPendingSetsByLog((prev) => {
          const next = { ...prev };
          delete next[trainingLogID];
          return next;
        });
        {
          const nextRef = { ...pendingSetsByLogRef.current };
          delete nextRef[trainingLogID];
          pendingSetsByLogRef.current = nextRef;
        }

        const totalSets = measurementList.length;
        const distinctActivities = new Set(
          measurementList.map((m) => m.ActivityTypeID),
        ).size;

        return {
          id: call.id,
          name: call.name,
          response: {
            success: true,
            sessionID,
            trainingLogID,
            plannedDate,
            totalSets,
            distinctActivities,
            invalidSessionType,
            message: `Saved session "${sessionName}" with ${totalSets} set${
              totalSets === 1 ? "" : "s"
            } across ${distinctActivities} activit${
              distinctActivities === 1 ? "y" : "ies"
            } and linked it to training log ${trainingLogID}. Pending-sets list cleared.`,
          },
        };
      } catch (err: any) {
        console.error("Failed to save session from Oly:", err);
        return {
          id: call.id,
          name: call.name,
          response: {
            success: false,
            error: err?.message ?? "Unknown error saving session.",
          },
        };
      }
    }

    return {
      id: call?.id,
      name: call?.name ?? "unknown",
      response: {
        success: false,
        error: `Unknown function: ${call?.name ?? "(none)"}`,
      },
    };
  };

  // Public handler passed to `startAudioConversation`. Bumps the pending
  // function-call counter around every invocation so the status label can
  // flip to "Thinking…" while tool calls are in flight. `endCall` is the
  // one tool where we intentionally skip the counter - it triggers a
  // teardown that will move `status` to "ending" anyway, which renders
  // its own label.
  const handleFunctionCalls = async (
    calls: FunctionCall[],
  ): Promise<FunctionResponse> => {
    const isEndCall = calls[0]?.name === "endCall";
    if (!isEndCall) {
      setPendingFunctionCalls((n) => n + 1);
    }
    try {
      return await runFunctionCall(calls);
    } finally {
      if (!isEndCall) {
        setPendingFunctionCalls((n) => Math.max(0, n - 1));
      }
    }
  };

  const startCall = async () => {
    if (status !== "idle") return;
    setError(null);
    setSavedLogs([]);
    setPendingSetsByLog({});
    setStatus("connecting");

    // Don't wipe the preload caches here: the background preload kicked off
    // when the dialog opened (see the `isOpen` effect below) may already
    // have completed or be in flight, and we want the AI's first tool call
    // to hit that cache instantly. Per-call resets happen on dialog close
    // via `resetChatState`. Kick the preloads again (no-op if already
    // cached) in case this is a reconnect within the same open dialog.
    void loadAthleteReport().catch((preloadError) => {
      console.warn(
        "Failed to preload athlete report for Oly call:",
        preloadError,
      );
    });
    void loadCallContext().catch((preloadError) => {
      console.warn(
        "Failed to preload activities/session types for Oly call:",
        preloadError,
      );
    });

    try {
      const liveModel = getLiveGenerativeModel(aiGoogle, {
        model: "gemini-3.1-flash-live-preview",
        generationConfig: {
          responseModalities: [ResponseModality.AUDIO],
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: { voiceName },
            },
          },
        },
        systemInstruction: buildSystemInstruction(new Date(), {
          isImperial,
        }),
        tools: OLY_TOOLS,
      });

      const session = await liveModel.connect();
      sessionRef.current = session;

      const controller = await startAudioConversation(session, {
        functionCallingHandler: handleFunctionCalls,
      });
      controllerRef.current = controller;

      // Nudge the model to take the first turn so the user hears Oly
      // greet them before they say anything. The system instruction
      // pins the exact opener ("Hey, how can I help you?") - this
      // just triggers that first turn.
      //
      // IMPORTANT: we MUST use `sendTextRealtime` (which sends a
      // `realtimeInput` message), NOT `session.send(..., true)` /
      // `clientContent`. The Live audio-preview model we connect to
      // rejects turn-based `clientContent` messages with a server
      // close ("Operation is not implemented, or supported, or
      // enabled"), which then surfaces as "WebSocket is not open"
      // errors from the mic audio worklet's `sendAudioRealtime` on
      // the next chunk. Realtime text input shares the same channel
      // as the audio streaming and is accepted; the server responds
      // with an audio turn that the AudioConversationController's
      // receive loop plays back through the speaker.
      try {
        await session.sendTextRealtime(
          "[System: the voice call has just connected. Speak now with your required opening line.]",
        );
      } catch (greetError) {
        console.warn(
          "Failed to send initial greeting trigger to Oly:",
          greetError,
        );
      }

      callStartedAtRef.current = Date.now();
      setCallDurationSec(0);
      durationTimerRef.current = setInterval(() => {
        if (callStartedAtRef.current) {
          setCallDurationSec(
            Math.floor((Date.now() - callStartedAtRef.current) / 1000),
          );
        }
      }, 1000);

      setStatus("active");
    } catch (startError: any) {
      console.error("Failed to start Oly voice call:", startError);
      const message =
        startError?.name === "NotAllowedError"
          ? "Microphone access was denied. Please enable it in your browser settings."
          : startError?.name === "NotFoundError"
            ? "No microphone was found. Please connect one and try again."
            : startError?.message ||
              "Could not start the voice call. Please try again.";
      setError(message);
      await teardown();
      setStatus("idle");
    }
  };

  const endCall = async () => {
    if (status === "idle") return;
    setStatus("ending");
    await teardown();
    setStatus("idle");
  };

  // Reset all state + caches back to their initial values so the next time
  // the dialog is opened it starts fresh (no leftover saved logs, pending
  // sets, cached report, error banner, etc.). Called when the dialog closes.
  const resetChatState = () => {
    setSavedLogs([]);
    setPendingSetsByLog({});
    setEditingLog(null);
    setError(null);
    setCallDurationSec(0);
    setPendingFunctionCalls(0);

    savedLogsRef.current = [];
    pendingSetsByLogRef.current = {};
    activityLookupRef.current = new Map();
    sessionTypeLookupRef.current = new Map();
    activitiesPromiseRef.current = null;
    reportCacheRef.current = null;
    reportPromiseRef.current = null;
  };

  // Keep the ref pointed at the latest endCall so the function-calling handler
  // (created once when the call starts) can always invoke the current closure.
  useEffect(() => {
    endCallRef.current = endCall;
  });

  const handleDialogClose = () => {
    if (status !== "idle") {
      void endCall().finally(() => {
        resetChatState();
        onClose();
      });
    } else {
      resetChatState();
      onClose();
    }
  };

  // Cleanup on dialog close or unmount. When the dialog is closed by the
  // parent (isOpen -> false) we both end any in-flight call and clear the
  // local UI state so reopening starts from a clean slate.
  useEffect(() => {
    if (!isOpen) {
      if (status !== "idle") {
        void endCall().finally(() => resetChatState());
      } else {
        resetChatState();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Warm the AI context caches the moment the dialog opens, well before the
  // user taps Call. The Firestore round-trips behind `getAthleteReport` and
  // `getActivitiesAndSessionTypes` can take a second or two, and waiting
  // until the model invokes those tools mid-call makes Oly feel sluggish.
  // We fire both fetches in the background, cache the results in the
  // existing refs, and let the usual on-demand handlers serve them
  // instantly. We do NOT forward these into the system instruction to
  // avoid bloating the live model's context window - they're only
  // surfaced when the model explicitly asks for them.
  useEffect(() => {
    if (!isOpen) return;
    if (!currentUser?.uid) return;
    void loadAthleteReport().catch((preloadError) => {
      console.warn("Failed to preload athlete report for Oly:", preloadError);
    });
    void loadCallContext().catch((preloadError) => {
      console.warn(
        "Failed to preload activities/session types for Oly:",
        preloadError,
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentUser?.uid]);

  useEffect(() => {
    return () => {
      void teardown();
    };
  }, []);

  const formatDuration = (totalSec: number) => {
    const mm = Math.floor(totalSec / 60)
      .toString()
      .padStart(2, "0");
    const ss = (totalSec % 60).toString().padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const statusLabel =
    status === "connecting"
      ? "Connecting…"
      : status === "active"
        ? pendingFunctionCalls > 0
          ? "Thinking…"
          : "Listening"
        : status === "ending"
          ? "Ending call…"
          : "Tap to start a voice call";

  const isCallActive = status === "active";
  const isBusy = status === "connecting" || status === "ending";
  const todayDateID = generateDateID(new Date());

  // Resolve an ActivityTypeID on a saved ActivityMeasurement to a display
  // name. Reads from the activity lookup populated by
  // `getActivitiesAndSessionTypes` (or lazily loaded during the pending-sets
  // flow). Falls back to a generic label when the lookup is empty or the
  // ID isn't found, so the table still renders sensibly.
  const getActivityName = (measurement: ActivityMeasurement): string => {
    const entry = activityLookupRef.current.get(measurement.ActivityTypeID);
    return entry?.name ?? "Activity";
  };

  return (
    <>
      <Transition appear show={isOpen}>
        <Dialog
          as="div"
          className="relative z-[60]"
          onClose={handleDialogClose}
        >
          <TransitionChild
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex h-full sm:min-h-full sm:items-center sm:justify-center sm:p-4">
              <TransitionChild
                enter="ease-out duration-200"
                enterFrom="opacity-0 sm:scale-95"
                enterTo="opacity-100 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 sm:scale-100"
                leaveTo="opacity-0 sm:scale-95"
              >
                <DialogPanel className="w-full h-full sm:h-[80vh] sm:max-h-[640px] sm:max-w-md transform overflow-hidden sm:rounded-2xl bg-base-100 shadow-xl transition-all flex flex-col">
                  {/* Header */}
                  <div className="flex items-center justify-between p-5">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-primary flex items-center justify-center">
                        <SparklesIcon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <DialogTitle className="text-base font-semibold text-base-content font-grotesk">
                          Oly
                        </DialogTitle>
                        <p className="text-xs text-base-content/60 font-grotesk">
                          Voice assistant · {voiceName}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleDialogClose}
                      className="p-2 hover:bg-base-200 rounded-full transition-colors"
                      title="Close"
                    >
                      <XMarkIcon className="h-5 w-5 text-base-content/60" />
                    </button>
                  </div>

                  {/* Scrollable middle area: call surface + saved notes */}
                  <div className="flex-1 overflow-y-auto flex flex-col">
                    {/* Call surface */}
                    <div className="flex flex-col items-center justify-center px-6 py-8 gap-6 flex-shrink-0 min-h-[60%]">
                      {/* Animated orb */}
                      <div className="relative flex items-center justify-center">
                        {isCallActive && (
                          <>
                            <span className="absolute h-48 w-48 rounded-full bg-primary/10 animate-ping z-50" />
                            <span className="absolute h-40 w-40 rounded-full bg-primary/20 animate-pulse z-50" />
                          </>
                        )}
                        {status === "connecting" && (
                          <span className="absolute h-40 w-40 rounded-full bg-primary/15 animate-pulse" />
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (status === "idle" && !isBusy) startCall();
                            else if (isCallActive) endCall();
                          }}
                          disabled={isBusy}
                          className={`relative h-32 w-32 rounded-full flex items-center justify-center shadow-lg transition-all ${
                            isCallActive
                              ? "bg-gradient-to-br from-primary to-primary/70 cursor-pointer"
                              : status === "connecting"
                                ? "bg-gradient-to-br from-primary/70 to-primary/40 cursor-not-allowed"
                                : "bg-gradient-to-br from-purple-500 to-primary hover:scale-105 hover:opacity-95 cursor-pointer"
                          } disabled:cursor-not-allowed`}
                        >
                          <img
                            src={peakIconBlack}
                            alt="Oly"
                            className={`h-16 w-16 transition-opacity ${
                              isCallActive
                                ? "opacity-100 invert"
                                : "opacity-100 invert"
                            }`}
                          />
                        </button>
                      </div>

                      {/* Status text */}
                      <div className="text-center">
                        <p className="text-lg font-semibold text-base-content font-grotesk">
                          {statusLabel}
                        </p>
                        {isCallActive && (
                          <p className="text-sm text-base-content/60 font-grotesk mt-1 tabular-nums">
                            {formatDuration(callDurationSec)}
                          </p>
                        )}
                        {status === "idle" && !error && (
                          <p className="text-sm text-base-content/60 font-grotesk mt-1 max-w-xs">
                            Speak naturally with Oly to create a log or ask a
                            question about your date.
                          </p>
                        )}
                      </div>

                      {/* Voice picker (only when idle) */}
                      {status === "idle" && (
                        <div className="flex flex-col items-center gap-1">
                          <label
                            htmlFor="oly-voice"
                            className="text-[10px] uppercase tracking-widest text-base-content/40 font-grotesk"
                          >
                            Voice
                          </label>
                          <select
                            id="oly-voice"
                            value={voiceName}
                            onChange={(e) => setVoiceName(e.target.value)}
                            className="appearance-none bg-base-200/60 border border-base-300 text-base-content text-sm text-center rounded-full px-10 py-2 font-grotesk font-medium focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer"
                          >
                            {VOICE_OPTIONS.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {error && (
                        <p
                          className="text-sm text-red-500 text-center font-grotesk max-w-xs"
                          role="alert"
                        >
                          {error}
                        </p>
                      )}
                    </div>

                    {/* Saved logs (added by Oly during this call) */}
                    {savedLogs.length > 0 && (
                      <div className="px-5 pb-5 flex-shrink-0">
                        <h3 className="text-xs uppercase tracking-wide text-base-content/60 font-grotesk text-center mb-2">
                          {savedLogs.length === 1
                            ? "Log added"
                            : `${savedLogs.length} logs added`}
                        </h3>
                        <div className="space-y-2">
                          {savedLogs.map((entry) => (
                            <SavedLogCard
                              key={`${entry.kind}-${entry.logID}`}
                              entry={entry}
                              todayDateID={todayDateID}
                              onEdit={(editing) => setEditingLog(editing)}
                              pendingSets={
                                entry.kind === "training"
                                  ? pendingSetsByLog[entry.logID]
                                  : undefined
                              }
                              getActivityName={getActivityName}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action button - only show end call when active */}
                  {(isCallActive || status === "ending") && (
                    <div className="p-6 flex items-center justify-center">
                      <button
                        type="button"
                        onClick={endCall}
                        disabled={status === "ending"}
                        className="h-16 w-16 rounded-full bg-red-500 text-white flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors disabled:opacity-60"
                        title="End call"
                      >
                        <PhoneXMarkIcon className="h-7 w-7" />
                      </button>
                    </div>
                  )}
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Edit forms — opened from saved log cards */}
      <AddNoteLogForm
        open={editingLog?.kind === "note"}
        onClose={() => setEditingLog(null)}
        onSubmit={async (notes) => {
          if (editingLog?.kind !== "note" || !currentUser?.uid) return;
          await updateNoteLog(currentUser.uid, editingLog.logID, { notes });
          await refreshSavedLog("note", editingLog.logID);
        }}
        dateID={editingLog?.kind === "note" ? editingLog.dateID : ""}
        editMode={editingLog?.kind === "note"}
        initialNotes={editingLog?.kind === "note" ? editingLog.log.notes : ""}
        logId={editingLog?.kind === "note" ? editingLog.logID : undefined}
      />

      <AddRecoveryLogForm
        open={editingLog?.kind === "recovery"}
        onClose={() => setEditingLog(null)}
        onSubmit={async (recoveryLog) => {
          if (editingLog?.kind !== "recovery" || !currentUser?.uid) return;
          await updateRecoveryLog(currentUser.uid, editingLog.logID, {
            selectedRecovery: recoveryLog.selectedRecovery,
            notes: recoveryLog.notes,
            pointsAmount: recoveryLog.pointsAmount,
          });
          await refreshSavedLog("recovery", editingLog.logID);
        }}
        dateID={editingLog?.kind === "recovery" ? editingLog.dateID : ""}
        editMode={editingLog?.kind === "recovery"}
        initialData={
          editingLog?.kind === "recovery"
            ? {
                selectedRecovery: editingLog.log.selectedRecovery,
                notes: editingLog.log.notes,
                pointsAmount: editingLog.log.pointsAmount ?? 0,
              }
            : undefined
        }
        logId={editingLog?.kind === "recovery" ? editingLog.logID : undefined}
      />

      <AddNiggleLogForm
        open={editingLog?.kind === "niggle"}
        onClose={() => setEditingLog(null)}
        onSubmit={async (niggleLog) => {
          if (editingLog?.kind !== "niggle" || !currentUser?.uid) return;
          await updateNiggleLog(currentUser.uid, editingLog.logID, {
            pain: niggleLog.pain,
            impact: niggleLog.impact,
            notes: niggleLog.notes,
            ...(niggleLog.linkedTrainingLogID
              ? { linkedTrainingLogID: niggleLog.linkedTrainingLogID }
              : {}),
          });
          await refreshSavedLog("niggle", editingLog.logID);
        }}
        dateID={editingLog?.kind === "niggle" ? editingLog.dateID : ""}
        editMode={editingLog?.kind === "niggle"}
        initialData={
          editingLog?.kind === "niggle"
            ? {
                impact: editingLog.log.impact,
                notes: editingLog.log.notes,
                pain: editingLog.log.pain,
              }
            : undefined
        }
        logId={editingLog?.kind === "niggle" ? editingLog.logID : undefined}
      />

      <AddPerformanceLogForm
        open={editingLog?.kind === "performance"}
        onClose={() => setEditingLog(null)}
        onSubmit={async (performanceLog) => {
          if (editingLog?.kind !== "performance" || !currentUser?.uid) {
            return editingLog?.logID ?? "";
          }
          await updatePerformanceLog(currentUser.uid, editingLog.logID, {
            notes: performanceLog.notes,
            performanceExpectation: performanceLog.performanceExpectation,
            performanceResult: performanceLog.performanceResult,
            performanceTitle: performanceLog.performanceTitle,
            performanceDate: performanceLog.performanceDate,
            ...(performanceLog.activity
              ? { activity: performanceLog.activity }
              : {}),
            ...(performanceLog.tag ? { tag: performanceLog.tag } : {}),
            ...(performanceLog.linkedSessionID
              ? { linkedSessionID: performanceLog.linkedSessionID }
              : {}),
            ...(performanceLog.linkedTrainingLogID
              ? { linkedTrainingLogID: performanceLog.linkedTrainingLogID }
              : {}),
          });
          await refreshSavedLog("performance", editingLog.logID);
          return editingLog.logID;
        }}
        dateID={editingLog?.kind === "performance" ? editingLog.dateID : ""}
        editMode={editingLog?.kind === "performance"}
        initialData={
          editingLog?.kind === "performance" ? editingLog.log : undefined
        }
        logId={
          editingLog?.kind === "performance" ? editingLog.logID : undefined
        }
      />

      <TrainingLogForm
        open={editingLog?.kind === "training"}
        onClose={() => setEditingLog(null)}
        context={{
          dateID: editingLog?.kind === "training" ? editingLog.dateID : "",
        }}
        editMode={editingLog?.kind === "training"}
        logId={editingLog?.kind === "training" ? editingLog.logID : undefined}
        initialData={
          editingLog?.kind === "training" ? editingLog.log : undefined
        }
        onSaved={async () => {
          if (editingLog?.kind === "training") {
            await refreshSavedLog("training", editingLog.logID);
          }
          setEditingLog(null);
        }}
      />
    </>
  );
};

export default OlyChat;
